import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { map, Observable, of, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { REQUEST_TIMEOUT_MS, withoutRequestTimeout } from '../interceptors/timeout.interceptor';
import { withoutOfflineOverlay } from '../interceptors/network-resilience.interceptor';
import { OrderImportRow, OrderImportResponse } from '../types/order-import.types';
import { BusinessApiToken, IssueBusinessApiTokenPayload, IssuedBusinessApiToken } from '../types/api-token.types';
import { SeasonCloseRequest, SeasonCloseResponse } from '../types/season.types';
import { AdminCreateBusinessPayload } from '../types/admin-business.types';
import {
  AdminRegisterRequest,
  ApproveInitialRegistrationResponse,
  AssignManagerResponse,
  BusinessDataRequest,
  BusinessListItem,
  BusinessRegistrationDraftRequest,
  BusinessRegistrationDraftResponse,
  BusinessStatusResponse,
  AdminBusinessesListParams, BusinessesListResponse,
  BusinessUpdateDataRequest,
  CurrentUser,
  FullBusinessDetails,
  InitialRegistration,
  InternalUsersListResponse,
  InviteAccountantRequest,
  InviteManagerRequest,
  InviteManagerResponse,
  LoginRequest,
  LogoutRequest,
  ManagerDetail,
  ManagersListResponse,
  ManagerWalletResponse,
  PasswordResetConfirmRequest,
  PasswordResetRequest,
  PasswordResetResponse,
  RegisterRequest,
  RegisterWithTokenRequest,
  RegisterWithTokenResponse,
  AuthInvitationPreview,
  IssuedTelegramLinkCode,
  RejectInitialRegistrationResponse,
  TelegramLinkStatus,
  TokenResponse,
  UserBusinessItem,
  UsersListResponse
} from '../types/auth.types';
import {
  PermissionCatalogResponse,
  SetUserPermissionsRequest,
  StaffPermissionsResponse,
  UserPermissionsResponse
} from '../types/permissions.types';
import {
  AdminCreatePromotionRequest,
  AdminPromocodeListResponse,
  AdminUpdatePromotionRequest,
  AuditLogListResponse,
  CreateDraftPromotionRequest,
  GetPromotionsParams,
  Offer,
  Promotion,
  PromotionStatus,
  PromotionVersionsResponse,
  TerminationAgreementResponse,
  TerminationAgreementPreview,
  PromocodeListResponse,
  UpdateDraftPromotionRequest,
  CreateTaskRequest,
  TaskResponse,
  BusinessProfileUpdate,
  ModerateProfileUpdateRequest,
  MarketplaceCategory,
  ManagerTicket,
  ManagerTicketKind,
  ManagerTicketStatus,
  ManagerTicketsListResponse,
  AdminCreateCategoryRequest,
  AdminUpdateCategoryRequest,
  CreateGeneralTicketRequest,
  CreateBusinessTicketRequest,
  ResolveTicketRequest,
  DismissTicketRequest,
  AssignTicketRequest,
  ScanPromocodeRequest,
  ScanPromocodeResponse,
  BusinessOrdersListResponse,
  GetBusinessOrdersParams,
  PromocodeSummary,
  GetPromocodeSummaryParams,
  PlayerStatsResponse,
  PaginatedPlayersResponse,
  GetAdminPlayersParams,
  PlayerView,
} from '../types/dashboard.types';
import {
  AdminBusinessReferrerTransferRequest,
  AdminBusinessReferrerTransferResponse,
  AdminReferralTransferRequest,
  AdminReferralTransferResponse,
  CalculatorResponse,
  CloseCurrentReferralPeriodRequest,
  GetCalculatorRowsParams,
  RecalculateMonthRequest,
  RecalculationResponse,
  AgentInvitedBusinessesResponse,
  AgentReferralSearchResponse,
  ReferralAgentResponse,
  ReferralPeriodClosureResponse,
  ReferralStructureResponse,
  ReferralTicketThresholdResponse,
  ReferralTicketThresholdUpdateRequest,
  ReserveFundResponse,
} from '../types/referral.types';
import {
  AgentProfile,
  GetPlayerOrdersParams,
  GetPlayerPromocodesParams,
  GetWalletHistoryParams,
  PlayerBalance,
  PlayerLevel,
  PlayerOrder,
  PlayerPromocode,
  WalletHistoryResponse,
} from '../types/player-card.types';
import {
  KubishkaResponse,
  KubishkaDetailResponse,
  CreateKubishkaRequest,
  UpdateKubishkaRequest,
  KubishkaParticipant,
  KubishkaDrawResponse,
  PrizeFundContribution,
  RaffleFairnessResponse,
  KubishkaCondition,
  CreateConditionRequest,
  UpdateConditionRequest,
  KubishkaRaffleTypeOption,
  KubishkaDistributionModeOption,
  KubishkaTicketTypeOption,
} from '../types/kubishka.types';
import {
  AdminApplicationsFilterParams,
  BusinessApplicationsFilterParams,
  OfferListResponse,
  PlatformDocument,
  PlatformDocumentDetail,
  PlatformDocumentListResponse,
  PlatformDocumentVersion,
} from '../types/document.types';
import { Shop, ShopDetail, ShopFormPayload, CreateShopPayload, GrantShopAccessPayload, ShopAccessRecord, ShopAccessMatrixItem, ShopModerationReviewPayload, AdminShopListQuery, AdminShopListResponse, AdminShopDetail, AdminCreateShopPayload, ShopStatus } from '../types/shop.types';
import {
  AccountingInvoicesQuery,
  BusinessAccountingSummaryResponse,
  BusinessInvoicesQuery,
  ConfirmInvoiceRequest,
  InvoiceDetail,
  InvoiceListResponse,
  InvoiceStatus,
  RequestAccountingInvoicePayload,
  RescheduleInvoiceRequest,
} from '../types/accounting.types';
import {
  AdminBusinessReport,
  AdminReportsQuery,
  BusinessReport,
} from '../types/report.types';
import {
  AdminNotificationRequest,
  AdminNotificationToPlayerRequest,
  CabinetNotification,
  CabinetUnreadCount,
  NotificationDispatchResult,
  NotificationsQuery,
} from '../types/notification.types';
import { OnboardingProgressResponse } from '../types/onboarding.types';
import {
  AutoMessageDetail,
  AutoMessageGeneralSettings,
  AutoMessageListResponse,
  AutoMessagePreviewRequest,
  AutoMessagePreviewResponse,
  AutoMessageTestSendRequest,
  AutoMessageTestSendResponse,
  AutoMessageUpdateRequest,
} from '../types/auto-message.types';
import { BusinessReferralStatus, IssuedClientAppLink } from '../types/cabinet-referral.types';
import {
  AnalyticsActivityResponse,
  AnalyticsCabinetInvoicesResponse,
  AnalyticsCabinetSummaryResponse,
  AnalyticsGameResponse,
  AnalyticsLotteryQuery,
  AnalyticsLotterySummaryResponse,
  AnalyticsModerationFunnelResponse,
  AnalyticsOrdersByChannelResponse,
  AnalyticsOrdersSummaryResponse,
  AnalyticsOrdersTopDimension,
  AnalyticsOrdersTopResponse,
  AnalyticsOverviewResponse,
  AnalyticsPeriod,
  AnalyticsPlayersResponse,
  AnalyticsReferralAgentsResponse,
  AnalyticsReferralMoneyResponse,
  AnalyticsRetentionResponse,
  AnalyticsSettlementMonth,
  AnalyticsTopAgentsDimension,
  AnalyticsTopAgentsResponse,
} from '../types/analytics.types';

/**
 * BE-1303: сколько держим запрос рассылки, мс.
 *
 * Отправка на бэке синхронная и последовательная (по два INSERT на игрока плюс поход во
 * внешний канал на каждого), поэтому дефолтные 30 с обрывают её уже на сотнях игроков.
 * Полностью снимать таймаут нельзя: `withoutRequestTimeout()` ставит ноль, и зависший
 * POST крутился бы вечно с незакрываемым спиннером. Десять минут — компромисс: покрывает
 * реальную аудиторию и всё-таки заканчивается.
 */
export const NOTIFICATION_DISPATCH_TIMEOUT_MS = 10 * 60_000;

/**
 * Сколько держим запрос закрытия сезона, мс.
 *
 * Закрытие идёт по всем игрокам партиями, на каждого — блокировка строки, запись
 * результата и две операции в Redis. Дефолтные 30 с оборвут его на первой же
 * заметной базе. Ноль (`withoutRequestTimeout`) не годится по той же причине,
 * что и у рассылки: зависший POST крутился бы вечно.
 */
export const SEASON_CLOSE_TIMEOUT_MS = 10 * 60_000;

/**
 * Контекст запроса рассылки: длинный таймаут и запрет полноэкранного «нет связи».
 * Обрыв на клиенте не отменяет рассылку на сервере — оверлей утверждал бы обратное и
 * провоцировал повторную отправку всей базе.
 */
function notificationDispatchContext(timeoutMs: number = REQUEST_TIMEOUT_MS.defaultValue()) {
  return withoutOfflineOverlay(new HttpContext().set(REQUEST_TIMEOUT_MS, timeoutMs));
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly API_BASE_URL = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  login(credentials: LoginRequest): Observable<TokenResponse> {
    return this.http.post<TokenResponse>(`${this.API_BASE_URL}/auth/login`, credentials);
  }

  register(userData: RegisterRequest): Observable<TokenResponse> {
    return this.http.post<TokenResponse>(`${this.API_BASE_URL}/auth/register`, userData);
  }

  refreshToken(refreshData: { refresh_token: string }): Observable<TokenResponse> {
    return this.http.post<TokenResponse>(
      `${this.API_BASE_URL}/auth/refresh`,
      { refresh_token: refreshData.refresh_token },
      { headers: this.getAuthHeaders() }
    );
  }

  /** `context` — например, без полноэкранного «нет связи» для фонового отзыва пары (BE-1490). */
  logout(logoutData: LogoutRequest, context?: HttpContext): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API_BASE_URL}/auth/logout`, {
      refresh_token: logoutData.refresh_token,
      access_token: logoutData.access_token
    }, {
      headers: this.getAuthHeaders(),
      context
    });
  }

  getCurrentUser(): Observable<CurrentUser> {
    return this.http.get<CurrentUser>(`${this.API_BASE_URL}/auth/me`, { headers: this.getAuthHeaders() });
  }

  resetPassword(request: PasswordResetRequest): Observable<PasswordResetResponse> {
    return this.http.post<PasswordResetResponse>(`${this.API_BASE_URL}/auth/password/reset`, request);
  }

  confirmPasswordReset(request: PasswordResetConfirmRequest): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.API_BASE_URL}/auth/password/reset/confirm`, request);
  }

  /** BE-560: предпросмотр приглашения сотрудника по токену. */
  getAuthInvitation(token: string): Observable<AuthInvitationPreview> {
    return this.http.get<AuthInvitationPreview>(`${this.API_BASE_URL}/auth/invitation`, {
      params: { token },
    });
  }

  /**
   * @deprecated BE-1242: отдаёт все строки разом, включая бизнес-аккаунты, без фильтров и
   * пагинации. Для внутренних пользователей — `getAdminUsers`. Остаётся источником
   * бизнес-аккаунтов и карточки: справочника по id у внутренних пока нет.
   */
  getUsers(): Observable<UsersListResponse> {
    return this.http.get<UsersListResponse>(`${this.API_BASE_URL}/auth/users`, { headers: this.getAuthHeaders() });
  }

  /**
   * BE-1242: справочник внутренних пользователей (менеджеры, бухгалтеры, админы).
   * Фильтр по ролям — повторяемый параметр `role`; поиск по фрагменту email (до 128
   * символов, регистр не важен); `limit` 1…200. Только ADMIN/ROOT.
   */
  getAdminUsers(params?: {
    role?: string[];
    search?: string;
    include_deleted?: boolean;
    limit?: number;
    offset?: number;
  }): Observable<InternalUsersListResponse> {
    let query = new HttpParams();
    (params?.role ?? []).forEach((role) => {
      query = query.append('role', role);
    });
    if (params?.search) query = query.set('search', params.search);
    if (params?.include_deleted !== undefined) query = query.set('include_deleted', String(params.include_deleted));
    if (params?.limit !== undefined) query = query.set('limit', String(params.limit));
    if (params?.offset !== undefined) query = query.set('offset', String(params.offset));
    return this.http.get<InternalUsersListResponse>(`${this.API_BASE_URL}/admin/users`, {
      params: query,
      headers: this.getAuthHeaders()
    });
  }

  registerUser(userData: AdminRegisterRequest): Observable<TokenResponse> {
    return this.http.post<TokenResponse>(`${this.API_BASE_URL}/auth/register`, userData, {
      headers: this.getAuthHeaders()
    });
  }

  /** GET /admin/managers — список менеджеров. API может вернуть items или managers. */
  getManagers(): Observable<ManagersListResponse> {
    return this.http.get<any>(`${this.API_BASE_URL}/admin/managers`, {
      headers: this.getAuthHeaders()
    }).pipe(
      map((res) => ({
        managers: res.managers ?? res.items ?? [],
        total: res.total ?? 0,
        items_from: res.items_from,
        items_to: res.items_to
      }))
    );
  }

  /** POST /admin/managers/invite — пригласить менеджера. Ответ 201: token, email, expires_at, registration_link. */
  inviteManager(data: InviteManagerRequest): Observable<InviteManagerResponse> {
    return this.http.post<InviteManagerResponse>(`${this.API_BASE_URL}/admin/managers/invite`, data, {
      headers: this.getAuthHeaders()
    });
  }

  // === BE-824: личная привязка Telegram внутреннего пользователя ===

  /** GET /admin/me/telegram — привязан ли Telegram у текущего сотрудника. */
  getMyTelegramLink(): Observable<TelegramLinkStatus> {
    return this.http.get<TelegramLinkStatus>(`${this.API_BASE_URL}/admin/me/telegram`, {
      headers: this.getAuthHeaders()
    });
  }

  /**
   * POST /admin/me/telegram/link — выдать одноразовый код и deep-link на внутреннего бота.
   * Тела у запроса нет: пользователь берётся из токена. 503 — бот не сконфигурирован на сервере.
   */
  issueTelegramLinkCode(): Observable<IssuedTelegramLinkCode> {
    return this.http.post<IssuedTelegramLinkCode>(`${this.API_BASE_URL}/admin/me/telegram/link`, null, {
      headers: this.getAuthHeaders()
    });
  }

  /** DELETE /admin/me/telegram — отвязать Telegram (204). Идемпотентно. */
  unlinkTelegram(): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/admin/me/telegram`, {
      headers: this.getAuthHeaders()
    });
  }

  /** DELETE /admin/managers/{manager_id} — удалить менеджера (204). 409 — есть закреплённые бизнесы. */
  deleteManager(managerId: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/admin/managers/${managerId}`, {
      headers: this.getAuthHeaders()
    });
  }

  /** GET /admin/managers/{manager_id} — информация о менеджере и закреплённых бизнесах. */
  getManagerById(managerId: string): Observable<ManagerDetail> {
    return this.http.get<ManagerDetail>(`${this.API_BASE_URL}/admin/managers/${managerId}`, {
      headers: this.getAuthHeaders()
    });
  }

  /**
   * BE-1249: PATCH /admin/managers/{manager_id} — включить/выключить вознаграждение менеджера
   * (доля 2 % по заказам его бизнесов). Только Админ, менеджеру 403. Ответ — карточка
   * менеджера с актуальным значением признака; новое значение действует на новые заказы.
   */
  updateManagerReward(managerId: string, enabled: boolean): Observable<ManagerDetail> {
    return this.http.patch<ManagerDetail>(
      `${this.API_BASE_URL}/admin/managers/${managerId}`,
      { manager_reward_enabled: enabled },
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * BE-1251: GET /admin/managers/{manager_id}/wallet — баланс менеджера и страница начислений.
   * Админ читает кошелёк любого менеджера, менеджер — только свой (на чужой id и на
   * несуществующий приходит 403, поэтому «не найдено» по нему писать нельзя).
   * limit 1…200 (default 100), offset от 0; сортировка — свежие сверху.
   */
  getManagerWallet(
    managerId: string,
    params?: { limit?: number; offset?: number }
  ): Observable<ManagerWalletResponse> {
    let query = new HttpParams();
    if (params?.limit !== undefined) query = query.set('limit', String(params.limit));
    if (params?.offset !== undefined) query = query.set('offset', String(params.offset));
    return this.http.get<ManagerWalletResponse>(
      `${this.API_BASE_URL}/admin/managers/${managerId}/wallet`,
      { params: query, headers: this.getAuthHeaders() }
    );
  }

  /**
   * BE-805: каталог прав — разделы с тумблерами и готовые наборы. Только ADMIN/ROOT (иначе 403).
   * Состав каталога — данные бэкенда: в пределах релиза не меняется, запрашиваем при открытии.
   */
  getPermissionCatalog(): Observable<PermissionCatalogResponse> {
    return this.http.get<PermissionCatalogResponse>(`${this.API_BASE_URL}/admin/permissions/catalog`, {
      headers: this.getAuthHeaders()
    });
  }

  /** BE-805: выданные права внутреннего пользователя, приходят в порядке каталога. */
  getUserPermissions(userId: string): Observable<UserPermissionsResponse> {
    return this.http.get<UserPermissionsResponse>(
      `${this.API_BASE_URL}/admin/users/${userId}/permissions`,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * BE-805: заменить набор прав целиком (пустой список снимает все права).
   * 403 — правка себя или недоступной роли, 404 — не внутренний пользователь,
   * 422 — в наборе ключ вне каталога, при этом не записано ничего.
   */
  setUserPermissions(userId: string, permissions: string[]): Observable<UserPermissionsResponse> {
    const body: SetUserPermissionsRequest = { permissions };
    return this.http.put<UserPermissionsResponse>(
      `${this.API_BASE_URL}/admin/users/${userId}/permissions`,
      body,
      { headers: this.getAuthHeaders() }
    );
  }

  getBusinesses(params?: AdminBusinessesListParams): Observable<any[]> {
    return this.getBusinessesPage(params).pipe(map((res) => res.items as any[]));
  }

  /**
   * BE-1379: тот же `GET /admin/businesses`, но с конвертом — `total`/`limit`/`offset`.
   *
   * Заведён рядом, а не вместо `getBusinesses`: у того семь вызовов и шесть спек, и всем
   * им нужен только массив. Конверт нужен там, где выдача обрезана и об этом надо сказать
   * пользователю («показаны первые N — уточните запрос»).
   */
  getBusinessesPage(params?: AdminBusinessesListParams): Observable<BusinessesListResponse> {
    let query = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          const apiKey = key === 'status' ? 'business_status' : key;
          query = query.set(apiKey, String(value));
        }
      });
    }
    return this.http.get<BusinessesListResponse>(`${this.API_BASE_URL}/admin/businesses`, {
      params: query,
      headers: this.getAuthHeaders()
    });
  }

  getBusinessById(businessId: string): Observable<FullBusinessDetails> {
    return this.http.get<FullBusinessDetails>(`${this.API_BASE_URL}/admin/businesses/${businessId}`, {
      headers: this.getAuthHeaders()
    });
  }

  createBusiness(data: AdminCreateBusinessPayload): Observable<FullBusinessDetails> {
    return this.http.post<FullBusinessDetails>(`${this.API_BASE_URL}/admin/businesses`, data, {
      headers: this.getAuthHeaders()
    });
  }

  updateBusiness(businessId: string, data: any): Observable<any> {
    return this.http.put<any>(`${this.API_BASE_URL}/admin/businesses/${businessId}`, data, {
      headers: this.getAuthHeaders()
    });
  }

  deleteBusinessData(id: string): Observable<any> {
    return this.http.delete<any>(`${this.API_BASE_URL}/admin/businesses/${id}`, {
      headers: this.getAuthHeaders()
    });
  }

  changeBusinessStatus(businessId: string, status: 'canceled' | 'active' | 'banned' | 'on_moderation' | 'no_data'): Observable<any> {
    const statusMap: Record<string, string> = {
      active: 'ACTIVE',
      banned: 'BANNED',
      on_moderation: 'ON_MODERATION',
      canceled: 'CANCELED',
      no_data: 'NO_DATA'
    };
    return this.http.put<any>(
      `${this.API_BASE_URL}/admin/businesses/${businessId}/status`,
      { business_status: statusMap[status] ?? status },
      { headers: this.getAuthHeaders() }
    );
  }

  assignManagerToBusiness(businessId: string, managerId: string | null): Observable<AssignManagerResponse> {
    return this.http.post<AssignManagerResponse>(
      `${this.API_BASE_URL}/admin/businesses/${businessId}/assign_manager`,
      { manager_id: managerId },
      { headers: this.getAuthHeaders() }
    );
  }

  getBusinessNotes(businessId: string): Observable<{ manager_note: string | null }> {
    return this.http.get<{ manager_note: string | null }>(`${this.API_BASE_URL}/admin/businesses/${businessId}/notes`, {
      headers: this.getAuthHeaders()
    });
  }

  updateBusinessNotes(businessId: string, manager_note: string): Observable<{ manager_note: string | null }> {
    return this.http.put<{ manager_note: string | null }>(
      `${this.API_BASE_URL}/admin/businesses/${businessId}/notes`,
      { manager_note },
      { headers: this.getAuthHeaders() }
    );
  }

  /** Роль в теле: business_user_role — enum "OWNER" | "MANAGER". */
  inviteBusinessUser(
    businessId: string,
    payload: { email: string; business_user_role: 'OWNER' | 'MANAGER' }
  ): Observable<{ token?: string; email: string; expires_at: string; registration_link?: string }> {
    return this.http.post<{ token: string; email: string; expires_at: string; registration_link: string }>(
      `${this.API_BASE_URL}/admin/businesses/${businessId}/invite_user`,
      payload,
      { headers: this.getAuthHeaders() }
    );
  }

  /** Предварительная регистрация (черновик): POST /businesses/register, без auth. Минимальные поля. */
  submitInitialRegistration(data: BusinessRegistrationDraftRequest): Observable<BusinessRegistrationDraftResponse> {
    return this.http.post<BusinessRegistrationDraftResponse>(`${this.API_BASE_URL}/businesses/register`, data);
  }

  /** Обновление профиля бизнеса (контекст текущего пользователя): PUT /businesses/{business_id}. */
  updateBusinessProfile(businessId: string, data: BusinessUpdateDataRequest): Observable<any> {
    return this.http.put<any>(`${this.API_BASE_URL}/businesses/${businessId}`, data, {
      headers: this.getAuthHeaders()
    });
  }

  /** Отправка полных данных профиля бизнеса после входа: получаем business_id и делаем PUT /businesses/{id}. */
  registerBusinessData(data: BusinessDataRequest): Observable<any> {
    return this.getMyBusinessId().pipe(
      switchMap((businessId) =>
        this.updateBusinessProfile(businessId, {
          company_type: data.company_type,
          sphere: data.sphere,
          phone: data.phone,
          email: data.email,
          ...(data.organization_name ? { organization_name: data.organization_name } : {}),
          ...(data.inn ? { inn: data.inn } : {}),
          ...(data.address ? { address: data.address } : {}),
          ...(data.city ? { city: data.city } : {}),
          full_name: data.full_name ?? undefined,
          ogrn: data.ogrn ?? undefined,
          bank_number: data.bank_number,
          bank_id_code: data.bank_id_code,
          bank_name: data.bank_name,
          correspondent_account: data.correspondent_account,
          passport_series: data.passport_series ?? undefined,
          passport_number: data.passport_number ?? undefined,
          issued_by: data.issued_by ?? undefined,
          issued_at: data.issued_at ?? undefined,
          issuer_code: data.issuer_code ?? undefined
        })
      )
    );
  }

  /** Единая регистрация по токену: POST /auth/register (любая роль — менеджер, бизнес и т.д.). */
  registerWithToken(data: RegisterWithTokenRequest): Observable<RegisterWithTokenResponse> {
    return this.http.post<RegisterWithTokenResponse>(`${this.API_BASE_URL}/auth/register`, {
      email: data.email,
      password: data.password,
      register_token: data.register_token,
      personal_data_consent: data.personal_data_consent
    });
  }

  createBusinessData(data: BusinessDataRequest): Observable<any> {
    return this.registerBusinessData(data);
  }

  /** Список анкет на модерации для админа — GET /admin/businesses (фильтр ON_MODERATION по возможности и на клиенте). */
  getInitialRegistrations(): Observable<InitialRegistration[]> {
    return this.getBusinesses({
      status: 'ON_MODERATION',
      limit: 200,
      offset: 0
    }).pipe(
      map((items) => {
        const list = (items || []) as BusinessListItem[];
        const onModeration = list.filter((i) => i.business_status === 'ON_MODERATION');
        return (onModeration.length ? onModeration : list).map((item) => ({
          id: item.id,
          organization_name: item.name ?? '',
          city: item.city ?? '',
          created_at: item.created_at,
          full_name: item.assigned_manager_name ?? '',
          phone: '',
          email: '',
          organization_description: '',
          discount_desc: '',
          country: undefined,
          comment: undefined,
          additional_phone: undefined,
          additional_email: undefined,
          whatsapp_number: undefined,
          telegram_username: undefined
        }));
      })
    );
  }

  approveInitialRegistration(dataId: string, email: string): Observable<ApproveInitialRegistrationResponse> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/admin/businesses/${dataId}/invite_user`,
      { email, business_user_role: 'OWNER' as const },
      { headers: this.getAuthHeaders() }
    ).pipe(
      map(() => ({
        token: '',
        data_id: dataId,
        email,
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
      }))
    );
  }

  rejectInitialRegistration(dataId: string, email: string, reason: string): Observable<RejectInitialRegistrationResponse> {
    return this.changeBusinessStatus(dataId, 'canceled').pipe(map(() => ({ email, reason })));
  }

  /** Список акций: по swagger GET /businesses/{business_id}/promotions. Без businessId — первый бизнес пользователя. */
  getPromotions(params?: GetPromotionsParams): Observable<{ promotions: Promotion[]; total: number }> {
    return this.getMyBusinessId().pipe(
      switchMap((businessId) => this.getUserBusinessPromotions(businessId, params))
    );
  }

  /** Удаление акции: DELETE /businesses/{business_id}/promotions/{promotion_id}. */
  deletePromotion(promotionId: string, businessId?: string | null): Observable<{ message: string }> {
    const id$ = businessId ? of(businessId) : this.getMyBusinessId();
    return id$.pipe(
      switchMap((id) =>
        this.http.delete<{ message: string }>(`${this.API_BASE_URL}/businesses/${id}/promotions/${promotionId}`, {
          headers: this.getAuthHeaders()
        })
      )
    );
  }

  /** Отправка черновика на модерацию: POST /businesses/{business_id}/promotions/{promotion_id}/submit-review. */
  submitPromotionForReview(promotionId: string, businessId?: string | null): Observable<Promotion> {
    const id$ = businessId ? of(businessId) : this.getMyBusinessId();
    return id$.pipe(
      switchMap((id) =>
        this.http.post<Promotion>(`${this.API_BASE_URL}/businesses/${id}/promotions/${promotionId}/submit-review`, {}, {
          headers: this.getAuthHeaders()
        })
      )
    );
  }

  /** @deprecated используйте submitPromotionForReview */
  publishPromotion(promotionId: string, businessId?: string | null): Observable<Promotion> {
    return this.submitPromotionForReview(promotionId, businessId);
  }

  /** Пауза акции: POST /businesses/{business_id}/promotions/{promotion_id}/pause. */
  pausePromotion(promotionId: string, businessId?: string | null): Observable<Promotion> {
    const id$ = businessId ? of(businessId) : this.getMyBusinessId();
    return id$.pipe(
      switchMap((id) =>
        this.http.post<Promotion>(`${this.API_BASE_URL}/businesses/${id}/promotions/${promotionId}/pause`, {}, {
          headers: this.getAuthHeaders()
        })
      )
    );
  }

  /** Возобновление акции: POST /businesses/{business_id}/promotions/{promotion_id}/resume. */
  resumePromotion(promotionId: string, businessId?: string | null): Observable<Promotion> {
    const id$ = businessId ? of(businessId) : this.getMyBusinessId();
    return id$.pipe(
      switchMap((id) =>
        this.http.post<Promotion>(`${this.API_BASE_URL}/businesses/${id}/promotions/${promotionId}/resume`, {}, {
          headers: this.getAuthHeaders()
        })
      )
    );
  }

  /**
   * Список промокодов: GET /businesses/{business_id}/promotions/{promotion_id}/promocodes.
   * BE-415: фильтры purchased/used — серверная фильтрация по статусу.
   */
  getPromocodes(
    promotionId: string,
    params?: { offset?: number; limit?: number; purchased?: boolean; used?: boolean },
    businessId?: string | null,
  ): Observable<PromocodeListResponse> {
    let query = new HttpParams();
    if (params?.offset !== undefined) query = query.set('offset', String(params.offset));
    if (params?.limit !== undefined) query = query.set('limit', String(params.limit));
    if (params?.purchased !== undefined) query = query.set('purchased', String(params.purchased));
    if (params?.used !== undefined) query = query.set('used', String(params.used));
    const id$ = businessId ? of(businessId) : this.getMyBusinessId();
    return id$.pipe(
      switchMap((id) =>
        this.http.get<PromocodeListResponse>(`${this.API_BASE_URL}/businesses/${id}/promotions/${promotionId}/promocodes`, {
          params: query,
          headers: this.getAuthHeaders()
        })
      )
    );
  }

  /** POST /businesses/promotions/promocodes/buy-test (swagger). */
  buyPromocodeTest(promocode: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.API_BASE_URL}/businesses/promotions/promocodes/buy-test`,
      null,
      { params: new HttpParams().set('promocode', promocode), headers: this.getAuthHeaders() }
    );
  }

  // ── Promotion CRUD (business) ──

  /** Создать черновик акции — POST /businesses/{business_id}/promotions */
  createDraftPromotion(businessId: string, data: CreateDraftPromotionRequest): Observable<Promotion> {
    return this.http.post<Promotion>(
      `${this.API_BASE_URL}/businesses/${businessId}/promotions`,
      data,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Обновить черновик акции — PUT /businesses/{business_id}/promotions/{promotion_id}.
   * Отдаёт сохранённую акцию (PromotionResponseSchema) — бэк может нормализовать
   * присланное (например, достроить схему у map_link), поэтому берём значение из ответа.
   */
  updateDraftPromotion(businessId: string, promotionId: string, data: UpdateDraftPromotionRequest): Observable<Promotion> {
    return this.http.put<Promotion>(
      `${this.API_BASE_URL}/businesses/${businessId}/promotions/${promotionId}`,
      data,
      { headers: this.getAuthHeaders() }
    );
  }

  /** Получить одну акцию бизнеса — GET /businesses/{business_id}/promotions/{promotion_id} */
  getUserBusinessPromotion(businessId: string, promotionId: string): Observable<Promotion> {
    return this.http.get<Promotion>(
      `${this.API_BASE_URL}/businesses/${businessId}/promotions/${promotionId}`,
      { headers: this.getAuthHeaders() }
    );
  }

  /** Подписать акцию (по promotion_id) — POST /businesses/{business_id}/promotions/{promotion_id}/sign */
  signPromotion(businessId: string, promotionId: string): Observable<Promotion> {
    return this.http.post<Promotion>(
      `${this.API_BASE_URL}/businesses/${businessId}/promotions/${promotionId}/sign`,
      {},
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Отказаться от подписи акции — POST /businesses/{business_id}/promotions/{promotion_id}/refuse-sign.
   * BE-727: тело обязательно содержит `reason` (RefuseSignRequestSchema: required, 1..2000).
   * Пустое тело → 422. BE-1085: по 200 акция возвращается в черновик (DRAFT),
   * актуальная Заявка гасится (PENDING→REPLACED).
   */
  refusePromotionSign(businessId: string, promotionId: string, reason: string): Observable<Promotion> {
    return this.http.post<Promotion>(
      `${this.API_BASE_URL}/businesses/${businessId}/promotions/${promotionId}/refuse-sign`,
      { reason },
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Отозвать Заявку без подписания (SIGNING → DRAFT, BE-1085) с причиной отказа.
   * BE-727: переиспользует refuse-sign — причину передаём в теле.
   */
  withdrawPromotionApplication(businessId: string, promotionId: string, reason: string): Observable<Promotion> {
    return this.refusePromotionSign(businessId, promotionId, reason);
  }

  /**
   * BE-1058: расторжение акции — POST .../terminate (тело пустое).
   * Возвращает акцию со статусом TERMINATED. 503 — не удалось сформировать соглашение (ретрай).
   */
  terminatePromotion(businessId: string, promotionId: string): Observable<Promotion> {
    return this.http.post<Promotion>(
      `${this.API_BASE_URL}/businesses/${businessId}/promotions/${promotionId}/terminate`,
      {},
      { headers: this.getAuthHeaders() },
    );
  }

  /** BE-1058: соглашение о расторжении (после terminate) — GET .../termination-agreement. */
  getTerminationAgreement(businessId: string, promotionId: string): Observable<TerminationAgreementResponse> {
    return this.http.get<TerminationAgreementResponse>(
      `${this.API_BASE_URL}/businesses/${businessId}/promotions/${promotionId}/termination-agreement`,
      { headers: this.getAuthHeaders() },
    );
  }

  /** BE-1058: HTML-предпросмотр соглашения (до расторжения) — GET .../termination-agreement/preview. */
  previewTerminationAgreement(businessId: string, promotionId: string): Observable<TerminationAgreementPreview> {
    return this.http.get<TerminationAgreementPreview>(
      `${this.API_BASE_URL}/businesses/${businessId}/promotions/${promotionId}/termination-agreement/preview`,
      { headers: this.getAuthHeaders() },
    );
  }

  /** BE-1058: PDF соглашения о расторжении — GET /files/terminations/{agreement_id}.pdf (по agreement_id). */
  downloadTerminationAgreementPdf(agreementId: string): Observable<Blob> {
    return this.http.get(`${this.API_BASE_URL}/files/terminations/${agreementId}.pdf`, {
      headers: this.getAuthHeaders(),
      responseType: 'blob',
    });
  }

  /** Скопировать акцию для создания нового черновика — POST /businesses/{business_id}/promotions/{promotion_id}/copy */
  copyPromotion(businessId: string, promotionId: string): Observable<Promotion> {
    return this.http.post<Promotion>(
      `${this.API_BASE_URL}/businesses/${businessId}/promotions/${promotionId}/copy`,
      {},
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * BE-1403: продлить завершённую акцию — POST /businesses/{business_id}/promotions/{promotion_id}/extend.
   *
   * Тело пустое, в ответе 201 — новый черновик с условиями исходной акции (без дат, файла
   * кодов и общего безлимитного кода). Отказы — 409: `promotion.not_expired` (акция не
   * завершена) и `promotion.not_latest_version` (у акции уже есть продолжение).
   */
  extendPromotion(businessId: string, promotionId: string): Observable<Promotion> {
    return this.http.post<Promotion>(
      `${this.API_BASE_URL}/businesses/${businessId}/promotions/${promotionId}/extend`,
      {},
      { headers: this.getAuthHeaders() }
    );
  }

  getBusinessPromotionOffer(businessId: string, promotionId: string): Observable<Offer> {
    return this.http.get<Offer>(
      `${this.API_BASE_URL}/businesses/${businessId}/promotions/${promotionId}/offer`,
      { headers: this.getAuthHeaders() }
    );
  }

  /** BE-435: список заявок бизнеса — GET /businesses/{business_id}/applications */
  listBusinessApplications(
    businessId: string,
    params?: BusinessApplicationsFilterParams,
  ): Observable<OfferListResponse> {
    let query = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          query = query.set(key, String(value));
        }
      });
    }
    return this.http.get<OfferListResponse>(
      `${this.API_BASE_URL}/businesses/${businessId}/applications`,
      { params: query, headers: this.getAuthHeaders() },
    );
  }

  /** @deprecated Используйте listBusinessApplications({ promotion_id }) */
  listBusinessPromotionOffers(
    businessId: string,
    promotionId: string,
    params?: { limit?: number; offset?: number }
  ): Observable<OfferListResponse> {
    let query = new HttpParams();
    if (params?.limit != null) query = query.set('limit', params.limit);
    if (params?.offset != null) query = query.set('offset', params.offset);
    return this.http.get<OfferListResponse>(
      `${this.API_BASE_URL}/businesses/${businessId}/promotions/${promotionId}/offers`,
      { params: query, headers: this.getAuthHeaders() }
    );
  }

  /** BE-433: список заявок (админ) — GET /admin/applications */
  listAdminApplications(params?: AdminApplicationsFilterParams): Observable<OfferListResponse> {
    let query = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          query = query.set(key, String(value));
        }
      });
    }
    return this.http.get<OfferListResponse>(`${this.API_BASE_URL}/admin/applications`, {
      params: query,
      headers: this.getAuthHeaders()
    });
  }

  /** BE-433: детали заявки — GET /admin/applications/{offer_id} */
  getAdminApplication(offerId: string): Observable<Offer> {
    return this.http.get<Offer>(`${this.API_BASE_URL}/admin/applications/${offerId}`, {
      headers: this.getAuthHeaders()
    });
  }

  /** BE-433: перевыпуск PDF заявки — POST /admin/applications/{offer_id}/regenerate */
  regenerateAdminApplication(offerId: string): Observable<Offer> {
    return this.http.post<Offer>(
      `${this.API_BASE_URL}/admin/applications/${offerId}/regenerate`,
      {},
      { headers: this.getAuthHeaders() }
    );
  }

  /** BE-433: скачать PDF заявки — GET /files/applications/{offer_id}.pdf */
  downloadApplicationPdf(offerId: string): Observable<Blob> {
    return this.http.get(`${this.API_BASE_URL}/files/applications/${offerId}.pdf`, {
      headers: this.getAuthHeaders(),
      responseType: 'blob'
    });
  }

  /** BE-433: открыть PDF заявки в новой вкладке (blob URL с авторизацией). */
  openApplicationPdf(offerId: string): Observable<string> {
    return this.downloadApplicationPdf(offerId).pipe(
      map((blob) => URL.createObjectURL(blob))
    );
  }

  /** BE-433: список типов документов платформы — GET /documents */
  listPublicPlatformDocuments(params?: { limit?: number; offset?: number }): Observable<PlatformDocumentListResponse> {
    let query = new HttpParams();
    if (params?.limit != null) query = query.set('limit', params.limit);
    if (params?.offset != null) query = query.set('offset', params.offset);
    return this.http.get<PlatformDocumentListResponse>(`${this.API_BASE_URL}/documents`, { params: query });
  }

  /** BE-433: документ по slug — GET /documents/{slug} */
  getPublicPlatformDocument(slug: string): Observable<PlatformDocument> {
    return this.http.get<PlatformDocument>(`${this.API_BASE_URL}/documents/${encodeURIComponent(slug)}`);
  }

  /** BE-433: PDF версии документа — GET /files/documents/{version_id}.pdf */
  downloadPlatformDocumentPdf(versionId: string): Observable<Blob> {
    return this.http.get(`${this.API_BASE_URL}/files/documents/${versionId}.pdf`, { responseType: 'blob' });
  }

  // === BE-454: админ — платформенные документы ===

  listAdminPlatformDocuments(params?: {
    include_deleted?: boolean;
    limit?: number;
    offset?: number;
  }): Observable<PlatformDocumentListResponse> {
    let query = new HttpParams();
    if (params?.include_deleted) query = query.set('include_deleted', 'true');
    if (params?.limit != null) query = query.set('limit', params.limit);
    if (params?.offset != null) query = query.set('offset', params.offset);
    return this.http.get<PlatformDocumentListResponse>(`${this.API_BASE_URL}/admin/documents`, {
      params: query,
      headers: this.getAuthHeaders(),
    });
  }

  getAdminPlatformDocument(documentId: string): Observable<PlatformDocumentDetail> {
    return this.http.get<PlatformDocumentDetail>(
      `${this.API_BASE_URL}/admin/documents/${documentId}`,
      { headers: this.getAuthHeaders() },
    );
  }

  createAdminPlatformDocument(formData: FormData): Observable<PlatformDocument> {
    return this.http.post<PlatformDocument>(`${this.API_BASE_URL}/admin/documents`, formData, {
      headers: this.getMultipartAuthHeaders(),
      context: withoutRequestTimeout(),
    });
  }

  updateAdminPlatformDocument(documentId: string, body: { title: string }): Observable<PlatformDocument> {
    return this.http.patch<PlatformDocument>(
      `${this.API_BASE_URL}/admin/documents/${documentId}`,
      body,
      { headers: this.getAuthHeaders() },
    );
  }

  deleteAdminPlatformDocument(documentId: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/admin/documents/${documentId}`, {
      headers: this.getAuthHeaders(),
    });
  }

  addAdminPlatformDocumentVersion(documentId: string, formData: FormData): Observable<PlatformDocumentVersion> {
    return this.http.post<PlatformDocumentVersion>(
      `${this.API_BASE_URL}/admin/documents/${documentId}/versions`,
      formData,
      { headers: this.getMultipartAuthHeaders(), context: withoutRequestTimeout() },
    );
  }

  deleteAdminPlatformDocumentVersion(documentId: string, versionId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.API_BASE_URL}/admin/documents/${documentId}/versions/${versionId}`,
      { headers: this.getAuthHeaders() },
    );
  }

  /** Получить акцию по ID — GET /admin/promotions/{promotion_id} */
  getAdminPromotion(promotionId: string): Observable<Promotion> {
    return this.http.get<Promotion>(`${this.API_BASE_URL}/admin/promotions/${promotionId}`, {
      headers: this.getAuthHeaders()
    });
  }

  /** Основной admin список акций — GET /admin/promotions. */
  getAdminPromotions(params?: GetPromotionsParams & { business_id?: string; title?: string; shop_id?: string }): Observable<{ promotions: Promotion[]; total: number }> {
    let query = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          query = query.set(key, String(value));
        }
      });
    }
    return this.http.get<{ promotions: Promotion[]; total: number }>(`${this.API_BASE_URL}/admin/promotions`, {
      params: query,
      headers: this.getAuthHeaders()
    });
  }

  /** Обновить акцию — PUT /admin/promotions/{promotion_id}. */
  updateAdminPromotion(promotionId: string, data: AdminUpdatePromotionRequest): Observable<Promotion> {
    return this.http.put<Promotion>(`${this.API_BASE_URL}/admin/promotions/${promotionId}`, data, {
      headers: this.getAuthHeaders()
    });
  }

  returnAdminPromotionToDraft(promotionId: string, moderation_comment: string): Observable<Promotion> {
    return this.http.post<Promotion>(
      `${this.API_BASE_URL}/admin/promotions/${promotionId}/return-to-draft`,
      { moderation_comment },
      { headers: this.getAuthHeaders() }
    );
  }

  sendAdminPromotionToSign(promotionId: string): Observable<Promotion> {
    return this.http.post<Promotion>(
      `${this.API_BASE_URL}/admin/promotions/${promotionId}/send-to-sign`,
      {},
      { headers: this.getAuthHeaders() }
    );
  }

  getAdminPromotionOffer(promotionId: string): Observable<Offer> {
    return this.http.get<Offer>(
      `${this.API_BASE_URL}/admin/promotions/${promotionId}/offer`,
      { headers: this.getAuthHeaders() }
    );
  }

  /** Приостановить акцию — POST /admin/promotions/{promotion_id}/pause */
  pauseAdminPromotion(promotionId: string): Observable<Promotion> {
    return this.http.post<Promotion>(`${this.API_BASE_URL}/admin/promotions/${promotionId}/pause`, {}, {
      headers: this.getAuthHeaders()
    });
  }

  /** Возобновить акцию — POST /admin/promotions/{promotion_id}/resume */
  resumeAdminPromotion(promotionId: string): Observable<Promotion> {
    return this.http.post<Promotion>(`${this.API_BASE_URL}/admin/promotions/${promotionId}/resume`, {}, {
      headers: this.getAuthHeaders()
    });
  }

  /**
   * BE-1098: принудительная смена статуса акции (админ/менеджер) — POST
   * /admin/promotions/{promotion_id}/status. Минует FSM и побочки. 409 — target PUBLISHED
   * (публикация только через подпись бизнесом), 403 — менеджер не по своему бизнесу.
   */
  forceAdminPromotionStatus(promotionId: string, status: PromotionStatus): Observable<Promotion> {
    return this.http.post<Promotion>(`${this.API_BASE_URL}/admin/promotions/${promotionId}/status`, { status }, {
      headers: this.getAuthHeaders()
    });
  }

  /** Список бизнесов текущего пользователя (GET /businesses). Для выбора кабинета. */
  getBusinessesForCurrentUser(): Observable<UserBusinessItem[]> {
    return this.http.get<any[]>(`${this.API_BASE_URL}/businesses`, { headers: this.getAuthHeaders() }).pipe(
      map((items) => {
        if (!Array.isArray(items)) return [];
        return items.map((item) => ({
          id: item.id ?? item.business_id,
          business_id: item.business_id ?? item.id,
          organization_name: item.organization_name ?? item.name ?? '',
          name: item.name ?? item.organization_name,
          // BE-1486: роль связи бэк шлёт заглавными (OWNER | MANAGER); «Владелец» — только при явном OWNER.
          role: String(item.role ?? '').toUpperCase() === 'OWNER' ? 'owner' : 'employee',
          status: (item.status ?? item.business_status ?? 'NO_DATA').toUpperCase()
        })) as UserBusinessItem[];
      })
    );
  }

  /** Данные бизнеса в контексте пользователя (GET /businesses/{business_id}). */
  getBusinessForUser(businessId: string): Observable<FullBusinessDetails> {
    return this.http.get<FullBusinessDetails>(`${this.API_BASE_URL}/businesses/${businessId}`, {
      headers: this.getAuthHeaders()
    });
  }

  /**
   * BE-707: прогресс онбординга бизнеса — единый источник для блока «Запуск бизнеса»
   * на Главной (статусы профиля / карточки / акции, процент, видимость блока).
   * GET /businesses/{business_id}/onboarding-progress.
   */
  getOnboardingProgress(businessId: string): Observable<OnboardingProgressResponse> {
    return this.http.get<OnboardingProgressResponse>(
      `${this.API_BASE_URL}/businesses/${businessId}/onboarding-progress`,
      { headers: this.getAuthHeaders() }
    );
  }

  /** Проверка принадлежности текущего пользователя к бизнесу (GET /businesses/{business_id}/check). */
  checkBusinessMembership(businessId: string): Observable<{ business_id: string; is_member: boolean; role?: string | null }> {
    return this.http.get<{ business_id: string; is_member: boolean; role?: string | null }>(
      `${this.API_BASE_URL}/businesses/${businessId}/check`,
      { headers: this.getAuthHeaders() }
    );
  }

  /** Список акций бизнеса в контексте пользователя (GET /businesses/{business_id}/promotions). */
  getUserBusinessPromotions(
    businessId: string,
    params?: GetPromotionsParams
  ): Observable<{ promotions: Promotion[]; total: number }> {
    let query = new HttpParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          query = query.set(key, String(value));
        }
      });
    }
    return this.http.get<{ promotions: Promotion[]; total: number }>(
      `${this.API_BASE_URL}/businesses/${businessId}/promotions`,
      { params: query, headers: this.getAuthHeaders() }
    );
  }

  /**
   * BE-1106: история цепочки версий акции —
   * GET /businesses/{business_id}/promotions/{promotion_id}/versions.
   * Ответ { versions: [...] } — от новой версии к старой, включая REPLACED.
   * Работает при передаче id любой версии цепочки.
   */
  getPromotionVersions(
    businessId: string,
    promotionId: string,
  ): Observable<PromotionVersionsResponse> {
    return this.http.get<PromotionVersionsResponse>(
      `${this.API_BASE_URL}/businesses/${businessId}/promotions/${promotionId}/versions`,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Отправка бизнеса на модерацию — POST /businesses/{business_id}/send_to_moderation.
   * Возвращает BusinessStatusResponseSchema ({ business_id, status }).
   * Ошибки:
   *  - 404 business.not_found — бизнес не найден
   *  - 409 business.invalid_state / business.not_ready — неверный статус или не заполнены обязательные поля
   *  - 422 validation — ошибки валидации
   */
  /** BE-469: тело — `{ offer_accepted: true }` (SendBusinessToModerationRequestSchema). */
  sendBusinessToModeration(
    businessId: string,
    body: { offer_accepted: boolean },
  ): Observable<BusinessStatusResponse> {
    return this.http.post<BusinessStatusResponse>(
      `${this.API_BASE_URL}/businesses/${businessId}/send_to_moderation`,
      body,
      { headers: this.getAuthHeaders() }
    );
  }

  // === BE-1346: связка игрового профиля владельца и партнёрские ссылки кабинета ===

  /**
   * GET /businesses/{business_id}/referral — статус связки и партнёрские ссылки.
   *
   * Пока связки нет, отвечает 200 с `is_linked: false` и пустыми ссылками — это штатное
   * состояние, а не 404. Доступ: владелец и сотрудники с правом `cabinet.profile.view`.
   */
  getBusinessReferral(businessId: string): Observable<BusinessReferralStatus> {
    return this.http.get<BusinessReferralStatus>(
      `${this.API_BASE_URL}/businesses/${businessId}/referral`,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * POST /businesses/{business_id}/client-app-link — одноразовая ссылка входа со связкой.
   *
   * Тела у запроса нет: бизнес в пути, пользователь в токене. Ответ не кэшируем — каждая
   * выдача гасит предыдущий токен, поэтому сохранённая ссылка уже нерабочая.
   * Ошибки: 403 — не владелец (и «Бизнес заблокирован.» у BANNED), 404 — нет членства
   * в этом бизнесе, 409 `business.player_already_linked` — профиль уже привязан.
   */
  createBusinessClientAppLink(businessId: string): Observable<IssuedClientAppLink> {
    return this.http.post<IssuedClientAppLink>(
      `${this.API_BASE_URL}/businesses/${businessId}/client-app-link`,
      null,
      { headers: this.getAuthHeaders() }
    );
  }

  // === BE-853: магазины кабинета бизнеса (Shops) ===

  /** Список магазинов бизнеса (GET /businesses/{id}/shops). OWNER видит все, MANAGER — только доступные. */
  getBusinessShops(businessId: string): Observable<Shop[]> {
    return this.http.get<Shop[]>(
      `${this.API_BASE_URL}/businesses/${businessId}/shops`,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * Карточка магазина (GET /businesses/{id}/shops/{shop_id}).
   * BE-881: single-GET дополнительно отдаёт picture_url (presigned-логотип), список — нет.
   */
  getBusinessShop(businessId: string, shopId: string): Observable<ShopDetail> {
    return this.http.get<ShopDetail>(
      `${this.API_BASE_URL}/businesses/${businessId}/shops/${shopId}`,
      { headers: this.getAuthHeaders() },
    );
  }

  /** Создать магазин (POST /businesses/{id}/shops). BE-956: доступно до активации — создаётся как черновик (DRAFT); лимит 10 → 409 shop.limit_reached. */
  createBusinessShop(businessId: string, body: CreateShopPayload): Observable<Shop> {
    return this.http.post<Shop>(
      `${this.API_BASE_URL}/businesses/${businessId}/shops`,
      body,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * BE-1102: создать магазин бизнесу админом/менеджером (POST /admin/businesses/{id}/shops).
   * Магазин создаётся сразу ACTIVE (минуя модерацию). marketplace_name обязателен (иначе 422);
   * лимит 10/юрлицо → 409 shop.limit_reached; менеджер не ведёт бизнес → 403.
   */
  createAdminShop(businessId: string, body: AdminCreateShopPayload): Observable<AdminShopDetail> {
    return this.http.post<AdminShopDetail>(
      `${this.API_BASE_URL}/admin/businesses/${businessId}/shops`,
      body,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * Редактировать карточку (PUT /businesses/{id}/shops/{shop_id}) → ShopDetailResponseSchema.
   * BE-904: DRAFT/REQUIRES_CHANGES — правки применяются in-place; ACTIVE — уходят в
   * черновик правок (draft), живая витрина не меняется. При pending на модерации →
   * 409 shop.moderation_in_progress.
   */
  updateBusinessShop(businessId: string, shopId: string, body: ShopFormPayload): Observable<ShopDetail> {
    return this.http.put<ShopDetail>(
      `${this.API_BASE_URL}/businesses/${businessId}/shops/${shopId}`,
      body,
      { headers: this.getAuthHeaders() },
    );
  }

  /** Удалить магазин — soft delete, статус → HIDDEN (DELETE /businesses/{id}/shops/{shop_id}). */
  deleteBusinessShop(businessId: string, shopId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.API_BASE_URL}/businesses/${businessId}/shops/${shopId}`,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * BE-904: сбросить черновик правок ACTIVE-магазина (DELETE /businesses/{id}/shops/{shop_id}/draft → 204).
   * Живая карточка на витрине не меняется. Нет черновика → 409 shop.no_draft.
   */
  deleteBusinessShopDraft(businessId: string, shopId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.API_BASE_URL}/businesses/${businessId}/shops/${shopId}/draft`,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * Отправить карточку на модерацию (POST /businesses/{id}/shops/{shop_id}/submit) → ShopDetailResponseSchema.
   * BE-904: DRAFT/REQUIRES_CHANGES (первая публикация) → ON_MODERATION; ACTIVE с черновиком →
   * draft переходит в pending(PENDING), магазин остаётся ACTIVE. Нет черновика → 409 shop.no_draft.
   */
  submitBusinessShop(businessId: string, shopId: string): Observable<ShopDetail> {
    return this.http.post<ShopDetail>(
      `${this.API_BASE_URL}/businesses/${businessId}/shops/${shopId}/submit`,
      {},
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * BE-857: модерация карточки магазина администратором
   * (POST /admin/shops/{shop_id}/moderate).
   * APPROVED → магазин становится ACTIVE; REJECTED/REQUIRES_CHANGES →
   * возврат в редактирование, rejection_reason обязателен.
   */
  moderateShop(shopId: string, body: ShopModerationReviewPayload): Observable<Shop> {
    return this.http.post<Shop>(
      `${this.API_BASE_URL}/admin/shops/${shopId}/moderate`,
      body,
      { headers: this.getAuthHeaders() },
    );
  }

  // === BE-880: админский экран «Магазины бизнеса» ===

  /**
   * Список магазинов (GET /admin/shops) — админ/менеджер.
   * Фильтры q/business_id/status, сортировка order_by/order_desc, пагинация limit/offset.
   * Ответ: { items, total, limit, offset }.
   */
  getAdminShops(query: AdminShopListQuery = {}): Observable<AdminShopListResponse> {
    let params = new HttpParams();
    if (query.q) params = params.set('q', query.q);
    if (query.business_id) params = params.set('business_id', query.business_id);
    if (query.status) params = params.set('status', query.status);
    if (query.order_by) params = params.set('order_by', query.order_by);
    if (query.order_desc !== undefined) params = params.set('order_desc', String(query.order_desc));
    if (query.limit !== undefined) params = params.set('limit', String(query.limit));
    if (query.offset !== undefined) params = params.set('offset', String(query.offset));
    return this.http.get<AdminShopListResponse>(
      `${this.API_BASE_URL}/admin/shops`,
      { headers: this.getAuthHeaders(), params },
    );
  }

  /**
   * Карточка магазина (GET /admin/shops/{shop_id}) — полная, без описания.
   * 404 → { code: "shop.not_found" }.
   */
  getAdminShop(shopId: string): Observable<AdminShopDetail> {
    return this.http.get<AdminShopDetail>(
      `${this.API_BASE_URL}/admin/shops/${shopId}`,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * BE-1052: админ-правка карточки магазина (PUT /admin/shops/{shop_id}).
   * Применяется СРАЗУ к живой карточке при любом статусе, БЕЗ черновика,
   * ре-модерации и смены статуса. Доступ: ADMIN/ROOT — всегда; MANAGER — только
   * для назначенного ему бизнеса (иначе 403). Шлём только изменённые поля:
   * пустое тело → 422 { code: "business.empty_update_payload" }.
   */
  updateAdminShop(shopId: string, body: ShopFormPayload): Observable<AdminShopDetail> {
    return this.http.put<AdminShopDetail>(
      `${this.API_BASE_URL}/admin/shops/${shopId}`,
      body,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * BE-1099: принудительная смена статуса магазина (админ/менеджер) — POST
   * /admin/shops/{shop_id}/status. Любой статус без ограничений, минуя модерацию.
   * 404 — нет магазина; 403 — менеджер не по своему бизнесу. Ответ — AdminShopDetailResponseSchema.
   */
  forceAdminShopStatus(shopId: string, status: ShopStatus): Observable<AdminShopDetail> {
    return this.http.post<AdminShopDetail>(
      `${this.API_BASE_URL}/admin/shops/${shopId}/status`,
      { status },
      { headers: this.getAuthHeaders() },
    );
  }

  // === BE-854: доступ менеджеров к магазинам (Shop access) ===

  /**
   * BE-955: авторитетная матрица доступа «магазин → менеджеры»
   * (GET /businesses/{id}/shops/access, owner-only). Возвращает строку по каждому
   * магазину бизнеса, в т.ч. с пустым `business_user_ids`. Идентификатор менеджера —
   * business_user_id (BusinessUser.id), НЕ user_id.
   * Ошибки: 401 без токена, 403 не владелец, 404 бизнес не найден.
   */
  getShopsAccess(businessId: string): Observable<ShopAccessMatrixItem[]> {
    return this.http.get<ShopAccessMatrixItem[]>(
      `${this.API_BASE_URL}/businesses/${businessId}/shops/access`,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * Выдать менеджеру доступ к магазину (POST /businesses/{id}/shops/{shop_id}/access).
   * `businessUserId` — id записи участия менеджера в бизнесе (BusinessUser.id), не user_id.
   * Ошибки: 409 (доступ уже выдан / владелец имеет доступ ко всем), 404 (не найдены бизнес/магазин/сотрудник).
   */
  grantShopAccess(businessId: string, shopId: string, businessUserId: string): Observable<ShopAccessRecord> {
    const body: GrantShopAccessPayload = { business_user_id: businessUserId };
    return this.http.post<ShopAccessRecord>(
      `${this.API_BASE_URL}/businesses/${businessId}/shops/${shopId}/access`,
      body,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * Отозвать у менеджера доступ к магазину
   * (DELETE /businesses/{id}/shops/{shop_id}/access/{business_user_id}).
   * Ошибки: 404 (доступ / бизнес / магазин / сотрудник не найдены).
   */
  revokeShopAccess(businessId: string, shopId: string, businessUserId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.API_BASE_URL}/businesses/${businessId}/shops/${shopId}/access/${businessUserId}`,
      { headers: this.getAuthHeaders() },
    );
  }

  // === BE-612: уведомления кабинета ===

  /** Бизнес-лента: GET /businesses/{id}/notifications. */
  getBusinessNotifications(businessId: string, q: NotificationsQuery = {}): Observable<CabinetNotification[]> {
    let params = new HttpParams();
    if (q.offset !== undefined) params = params.set('offset', String(q.offset));
    if (q.limit !== undefined) params = params.set('limit', String(q.limit));
    if (q.unread_only !== undefined) params = params.set('unread_only', String(q.unread_only));
    return this.http.get<CabinetNotification[]>(
      `${this.API_BASE_URL}/businesses/${businessId}/notifications`,
      { params, headers: this.getAuthHeaders() },
    );
  }

  getBusinessNotificationsCount(businessId: string): Observable<CabinetUnreadCount> {
    return this.http.get<CabinetUnreadCount>(
      `${this.API_BASE_URL}/businesses/${businessId}/notifications/count`,
      { headers: this.getAuthHeaders() },
    );
  }

  /** Пометить прочитанными; ids=null → все. */
  readBusinessNotifications(businessId: string, ids: number[] | null): Observable<CabinetUnreadCount> {
    return this.http.post<CabinetUnreadCount>(
      `${this.API_BASE_URL}/businesses/${businessId}/notifications/read`,
      { notification_ids: ids },
      { headers: this.getAuthHeaders() },
    );
  }

  /** Админ/менеджер-лента: GET /admin/me/notifications. */
  getAdminNotifications(q: NotificationsQuery = {}): Observable<CabinetNotification[]> {
    let params = new HttpParams();
    if (q.offset !== undefined) params = params.set('offset', String(q.offset));
    if (q.limit !== undefined) params = params.set('limit', String(q.limit));
    if (q.unread_only !== undefined) params = params.set('unread_only', String(q.unread_only));
    return this.http.get<CabinetNotification[]>(
      `${this.API_BASE_URL}/admin/me/notifications`,
      { params, headers: this.getAuthHeaders() },
    );
  }

  getAdminNotificationsCount(): Observable<CabinetUnreadCount> {
    return this.http.get<CabinetUnreadCount>(
      `${this.API_BASE_URL}/admin/me/notifications/count`,
      { headers: this.getAuthHeaders() },
    );
  }

  readAdminNotifications(ids: number[] | null): Observable<CabinetUnreadCount> {
    return this.http.post<CabinetUnreadCount>(
      `${this.API_BASE_URL}/admin/me/notifications/read`,
      { notification_ids: ids },
      { headers: this.getAuthHeaders() },
    );
  }

  // === BE-1176 + BE-1303: рассылка уведомлений игрокам ===

  /** Всем игрокам платформы. Действие массовое и необратимое — отозвать разосланное нечем. */
  broadcastPlayerNotification(payload: AdminNotificationRequest): Observable<NotificationDispatchResult> {
    return this.http.post<NotificationDispatchResult>(
      `${this.API_BASE_URL}/admin/notifications/broadcast`,
      payload,
      { headers: this.getAuthHeaders(), context: notificationDispatchContext(NOTIFICATION_DISPATCH_TIMEOUT_MS) },
    );
  }

  /**
   * Адресно одному игроку: та же схема плюс обязательный player_id. Таймаут дефолтный —
   * это одна запись и один поход во внешний канал, и держать кнопку десять минут незачем.
   */
  createPlayerNotification(payload: AdminNotificationToPlayerRequest): Observable<NotificationDispatchResult> {
    return this.http.post<NotificationDispatchResult>(
      `${this.API_BASE_URL}/admin/notifications`,
      payload,
      { headers: this.getAuthHeaders(), context: notificationDispatchContext() },
    );
  }

  // === BE-1520: каталог автосообщений (только Админ и ROOT) ===

  /** Ключ вида `business.registration_welcome` — в пути отдельным сегментом. */
  private autoMessageUrl(key: string, suffix = ''): string {
    return `${this.API_BASE_URL}/admin/auto-messages/${encodeURIComponent(key)}${suffix}`;
  }

  getAdminAutoMessages(): Observable<AutoMessageListResponse> {
    return this.http.get<AutoMessageListResponse>(`${this.API_BASE_URL}/admin/auto-messages`, {
      headers: this.getAuthHeaders(),
    });
  }

  getAdminAutoMessage(key: string): Observable<AutoMessageDetail> {
    return this.http.get<AutoMessageDetail>(this.autoMessageUrl(key), { headers: this.getAuthHeaders() });
  }

  /** Полная замена каналов, текстов и параметров; вкл/выкл не меняется. */
  updateAdminAutoMessage(key: string, payload: AutoMessageUpdateRequest): Observable<AutoMessageDetail> {
    return this.http.put<AutoMessageDetail>(this.autoMessageUrl(key), payload, { headers: this.getAuthHeaders() });
  }

  /** Только переключатель — правки текстов не затирает. */
  setAdminAutoMessageEnabled(key: string, enabled: boolean): Observable<AutoMessageDetail> {
    return this.http.patch<AutoMessageDetail>(this.autoMessageUrl(key, '/enabled'), { enabled }, {
      headers: this.getAuthHeaders(),
    });
  }

  /** Превью несохранённой правки; незаданное бэк берёт из сохранённых настроек. */
  previewAdminAutoMessage(key: string, payload: AutoMessagePreviewRequest): Observable<AutoMessagePreviewResponse> {
    return this.http.post<AutoMessagePreviewResponse>(this.autoMessageUrl(key, '/preview'), payload, {
      headers: this.getAuthHeaders(),
    });
  }

  /** Тестовая отправка по СОХРАНЁННЫМ текстам и каналам; в журнал не пишется. */
  testSendAdminAutoMessage(key: string, payload: AutoMessageTestSendRequest): Observable<AutoMessageTestSendResponse> {
    return this.http.post<AutoMessageTestSendResponse>(this.autoMessageUrl(key, '/test-send'), payload, {
      headers: this.getAuthHeaders(),
    });
  }

  getAdminAutoMessageSettings(): Observable<AutoMessageGeneralSettings> {
    return this.http.get<AutoMessageGeneralSettings>(`${this.API_BASE_URL}/admin/auto-messages-settings`, {
      headers: this.getAuthHeaders(),
    });
  }

  updateAdminAutoMessageSettings(payload: AutoMessageGeneralSettings): Observable<AutoMessageGeneralSettings> {
    return this.http.put<AutoMessageGeneralSettings>(`${this.API_BASE_URL}/admin/auto-messages-settings`, payload, {
      headers: this.getAuthHeaders(),
    });
  }

  /**
   * BE-629: нужно ли показать форму витрины перед публикацией акции.
   * GET /businesses/{business_id}/marketplace/required → { required }.
   * required=true — нет одобренного marketplace_name И нет PENDING-заявки.
   */
  getBusinessMarketplaceRequired(businessId: string): Observable<{ required: boolean }> {
    return this.http.get<{ required: boolean }>(
      `${this.API_BASE_URL}/businesses/${businessId}/marketplace/required`,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * BE-576: очистить черновик витрины (DELETE /businesses/{business_id}/marketplace/draft).
   * Требует ACTIVE. Идемпотентно, ответ 204.
   */
  deleteBusinessMarketplaceDraft(businessId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.API_BASE_URL}/businesses/${businessId}/marketplace/draft`,
      { headers: this.getAuthHeaders() },
    );
  }

  // ======= API-токены бизнеса (раздел «Интеграции») =======

  /** Список API-токенов бизнеса, живые и отозванные (GET /businesses/{id}/api-tokens). */
  getBusinessApiTokens(businessId: string): Observable<BusinessApiToken[]> {
    return this.http.get<BusinessApiToken[]>(
      `${this.API_BASE_URL}/businesses/${businessId}/api-tokens`,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * Выпуск API-токена (POST /businesses/{id}/api-tokens → 201). Секрет приходит только в
   * этом ответе; 409 `business.api_token_limit_reached` — исчерпан лимит живых токенов.
   */
  createBusinessApiToken(businessId: string, body: IssueBusinessApiTokenPayload): Observable<IssuedBusinessApiToken> {
    return this.http.post<IssuedBusinessApiToken>(
      `${this.API_BASE_URL}/businesses/${businessId}/api-tokens`,
      body,
      { headers: this.getAuthHeaders() },
    );
  }

  /** Отзыв API-токена (DELETE /businesses/{id}/api-tokens/{token_id} → 204; повторно — 404). */
  revokeBusinessApiToken(businessId: string, tokenId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.API_BASE_URL}/businesses/${businessId}/api-tokens/${tokenId}`,
      { headers: this.getAuthHeaders() },
    );
  }

  /** Список сотрудников бизнеса (GET /businesses/{business_id}/users). */
  getBusinessUsers(businessId: string): Observable<Array<{ id: string; user_id: string; email: string; role: string; last_login_at?: string }>> {
    return this.http.get<Array<{ id: string; user_id: string; email: string; role: string; last_login_at?: string }>>(
      `${this.API_BASE_URL}/businesses/${businessId}/users`,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * BE-807: каталог прав кабинета — разделы с тумблерами и готовые наборы для выдачи
   * сотруднику. Только владелец бизнеса (иначе 403). Внутренние права платформы сюда
   * не попадают: у них свой каталог в админке.
   */
  getBusinessPermissionCatalog(businessId: string): Observable<PermissionCatalogResponse> {
    return this.http.get<PermissionCatalogResponse>(
      `${this.API_BASE_URL}/businesses/${businessId}/permissions/catalog`,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * BE-807: права сотрудника в этом бизнесе, приходят в порядке каталога.
   * Ждёт `user_id` из строки списка сотрудников, а не её `id` — тот принадлежит записи
   * участия и используется матрицей доступа к магазинам.
   * 409 `business.owner_permissions_not_editable` — права владельца не редактируются,
   * 404 `business.staff_not_found` — человека в этом бизнесе нет либо он откреплён.
   */
  getBusinessUserPermissions(businessId: string, userId: string): Observable<StaffPermissionsResponse> {
    return this.http.get<StaffPermissionsResponse>(
      `${this.API_BASE_URL}/businesses/${businessId}/users/${userId}/permissions`,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * BE-807: заменить набор прав сотрудника целиком (пустой список снимает все права).
   * 422 `auth.permission_key_unknown` — в наборе ключ вне каталога кабинета, при этом
   * не записано ничего. Снятие права действует немедленно, без релогина сотрудника.
   */
  setBusinessUserPermissions(
    businessId: string,
    userId: string,
    permissions: string[]
  ): Observable<StaffPermissionsResponse> {
    const body: SetUserPermissionsRequest = { permissions };
    return this.http.put<StaffPermissionsResponse>(
      `${this.API_BASE_URL}/businesses/${businessId}/users/${userId}/permissions`,
      body,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Приглашение сотрудника (POST /businesses/{business_id}/invitations).
   *
   * BE-807: `permission_preset` — код набора прав из каталога кабинета. Поле бэкенду
   * необязательно, но без него сотрудник регистрируется вовсе без прав, поэтому в форме
   * выбор обязателен: «без прав» — это отдельный осознанный вариант, а не умолчание.
   * Неизвестный код → 422 `auth.permission_preset_unknown`.
   */
  inviteBusinessUserByOwner(businessId: string, payload: { email: string; business_user_role: 'OWNER' | 'MANAGER'; permission_preset?: string | null }): Observable<{ token: string; email: string; expires_at: string; registration_link?: string }> {
    return this.http.post<{ token: string; email: string; expires_at: string; registration_link?: string }>(
      `${this.API_BASE_URL}/businesses/${businessId}/invitations`,
      payload,
      { headers: this.getAuthHeaders() }
    );
  }

  /** Удаление сотрудника (DELETE /businesses/{business_id}/users/{user_id}). */
  removeBusinessUser(businessId: string, userId: string): Observable<string> {
    return this.http.delete<string>(`${this.API_BASE_URL}/businesses/${businessId}/users/${userId}`, {
      headers: this.getAuthHeaders()
    });
  }

  /** Отчётность: загрузка (POST) и список (GET) /businesses/{business_id}/reports. */
  uploadBusinessReport(businessId: string, formData: FormData): Observable<BusinessReport> {
    return this.http.post<BusinessReport>(`${this.API_BASE_URL}/businesses/${businessId}/reports`, formData, {
      headers: this.getMultipartAuthHeaders(),
      context: withoutRequestTimeout(),
    });
  }

  /** BE-608: список отчётов бизнеса — GET /businesses/{id}/reports (date_from/date_to обязательны). */
  getBusinessReports(businessId: string, dateFrom: string, dateTo: string): Observable<BusinessReport[]> {
    const params = new HttpParams().set('date_from', dateFrom).set('date_to', dateTo);
    return this.http.get<BusinessReport[]>(`${this.API_BASE_URL}/businesses/${businessId}/reports`, {
      params,
      headers: this.getAuthHeaders()
    });
  }

  // === BE-610: отчёты в админке ===

  /** Отчёты конкретного бизнеса (карточка бизнеса) — GET /admin/businesses/{id}/reports. */
  getAdminBusinessReports(businessId: string, dateFrom: string, dateTo: string, isReviewed?: boolean): Observable<AdminBusinessReport[]> {
    let params = new HttpParams().set('date_from', dateFrom).set('date_to', dateTo);
    if (isReviewed !== undefined) params = params.set('is_reviewed', String(isReviewed));
    return this.http.get<AdminBusinessReport[]>(`${this.API_BASE_URL}/admin/businesses/${businessId}/reports`, {
      params,
      headers: this.getAuthHeaders()
    });
  }

  /** Общий список отчётов — GET /admin/reports. ADMIN — все; MANAGER — только свои бизнесы. */
  getAdminReports(query: AdminReportsQuery): Observable<AdminBusinessReport[]> {
    let params = new HttpParams().set('date_from', query.date_from).set('date_to', query.date_to);
    if (query.business_id) params = params.set('business_id', query.business_id);
    if (query.is_reviewed !== undefined) params = params.set('is_reviewed', String(query.is_reviewed));
    return this.http.get<AdminBusinessReport[]>(`${this.API_BASE_URL}/admin/reports`, {
      params,
      headers: this.getAuthHeaders()
    });
  }

  /** Отметить отчёт проверенным / снять отметку — PATCH /admin/reports/{id}/review. */
  reviewAdminReport(reportId: string, isReviewed: boolean): Observable<AdminBusinessReport> {
    return this.http.patch<AdminBusinessReport>(
      `${this.API_BASE_URL}/admin/reports/${reportId}/review`,
      { is_reviewed: isReviewed },
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * BE-1020: батч-импорт заказов из отчёта — POST /admin/orders/import.
   * Тело — JSON-массив строк. Ответ всегда 200; разбирать по results[].status.
   */
  importOrders(rows: OrderImportRow[]): Observable<OrderImportResponse> {
    return this.http.post<OrderImportResponse>(
      `${this.API_BASE_URL}/admin/orders/import`,
      rows,
      { headers: this.getAuthHeaders() }
    );
  }

  private getMyBusinessId(): Observable<string> {
    return this.getBusinessesForCurrentUser().pipe(
      map((items) => {
        const first = items?.[0];
        const id = first?.id || first?.business_id;
        if (!id) {
          throw new Error('Business not found for current user');
        }
        return id;
      })
    );
  }

  // === Аудит логи ===

  getAuditLogs(params?: { loggable_type?: string; loggable_id?: string; limit?: number; offset?: number }): Observable<AuditLogListResponse> {
    let httpParams = new HttpParams();
    if (params?.loggable_type) httpParams = httpParams.set('loggable_type', params.loggable_type);
    if (params?.loggable_id) httpParams = httpParams.set('loggable_id', params.loggable_id);
    if (params?.limit) httpParams = httpParams.set('limit', params.limit);
    if (params?.offset != null) httpParams = httpParams.set('offset', params.offset);
    return this.http.get<AuditLogListResponse>(
      `${this.API_BASE_URL}/admin/audit_logs`,
      { headers: this.getAuthHeaders(), params: httpParams }
    );
  }

  // === Задачи (Tasks) ===

  getTasks(): Observable<TaskResponse[]> {
    return this.http.get<TaskResponse[]>(
      `${this.API_BASE_URL}/tasks`,
      { headers: this.getAuthHeaders() }
    );
  }

  createTask(request: CreateTaskRequest): Observable<TaskResponse> {
    return this.http.post<TaskResponse>(
      `${this.API_BASE_URL}/admin/tasks`,
      request,
      { headers: this.getAuthHeaders() }
    );
  }

  deactivateTask(taskId: string): Observable<{}> {
    return this.http.delete<{}>(
      `${this.API_BASE_URL}/admin/tasks/${taskId}`,
      { headers: this.getAuthHeaders() }
    );
  }

  // === Промокоды (Admin) ===

  getAdminPromocodes(params?: {
    promotion_id?: string; business_id?: string; shop_id?: string;
    purchased?: boolean; used?: boolean;
    limit?: number; offset?: number;
  }): Observable<AdminPromocodeListResponse> {
    let httpParams = new HttpParams();
    if (params?.promotion_id) httpParams = httpParams.set('promotion_id', params.promotion_id);
    if (params?.business_id) httpParams = httpParams.set('business_id', params.business_id);
    // BE-858: фильтр по магазину.
    if (params?.shop_id) httpParams = httpParams.set('shop_id', params.shop_id);
    if (params?.purchased != null) httpParams = httpParams.set('purchased', params.purchased);
    if (params?.used != null) httpParams = httpParams.set('used', params.used);
    if (params?.limit) httpParams = httpParams.set('limit', params.limit);
    if (params?.offset != null) httpParams = httpParams.set('offset', params.offset);
    return this.http.get<AdminPromocodeListResponse>(
      `${this.API_BASE_URL}/admin/promocodes`,
      { headers: this.getAuthHeaders(), params: httpParams }
    );
  }

  buyTestPromocode(promotionId: string, playerId: string): Observable<any> {
    return this.http.post<any>(
      `${this.API_BASE_URL}/admin/promotions/${promotionId}/buy-test/${playerId}`,
      {},
      { headers: this.getAuthHeaders() }
    );
  }

  // === Модерация обновлений профиля бизнеса ===

  getBusinessProfileUpdates(businessId: string, status?: string): Observable<BusinessProfileUpdate[]> {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    return this.http.get<BusinessProfileUpdate[]>(
      `${this.API_BASE_URL}/admin/businesses/${businessId}/updates`,
      { headers: this.getAuthHeaders(), params }
    );
  }

  moderateBusinessProfileUpdate(businessId: string, updateId: string, request: ModerateProfileUpdateRequest): Observable<BusinessProfileUpdate> {
    return this.http.post<BusinessProfileUpdate>(
      `${this.API_BASE_URL}/admin/businesses/${businessId}/updates/${updateId}/moderate`,
      request,
      { headers: this.getAuthHeaders() }
    );
  }

  // === Создание акции от лица админа ===

  createAdminPromotion(businessId: string, data: AdminCreatePromotionRequest): Observable<Promotion> {
    return this.http.post<Promotion>(
      `${this.API_BASE_URL}/admin/businesses/${businessId}/promotions`,
      data,
      { headers: this.getAuthHeaders() }
    );
  }

  // === Бизнес: marketplace updates ===

  getBusinessMarketplaceUpdates(businessId: string, status?: string): Observable<BusinessProfileUpdate[]> {
    let params = new HttpParams();
    if (status) params = params.set('status', status);
    return this.http.get<BusinessProfileUpdate[]>(
      `${this.API_BASE_URL}/businesses/${businessId}/marketplace/updates`,
      { headers: this.getAuthHeaders(), params }
    );
  }

  /** BE-473: заявки на изменение витринного профиля (админ). */
  getAdminMarketplaceUpdates(
    businessId: string,
    options?: { status?: string; limit?: number; offset?: number },
  ): Observable<BusinessProfileUpdate[]> {
    let params = new HttpParams()
      .set('limit', String(options?.limit ?? 50))
      .set('offset', String(options?.offset ?? 0))
      .set('order_by', 'created_at')
      .set('order_desc', 'true');
    if (options?.status) params = params.set('status', options.status);
    return this.http.get<BusinessProfileUpdate[]>(
      `${this.API_BASE_URL}/admin/businesses/${businessId}/marketplace/updates`,
      { headers: this.getAuthHeaders(), params },
    );
  }

  moderateAdminMarketplaceUpdate(
    businessId: string,
    updateId: string,
    request: ModerateProfileUpdateRequest,
  ): Observable<BusinessProfileUpdate> {
    return this.http.post<BusinessProfileUpdate>(
      `${this.API_BASE_URL}/admin/businesses/${businessId}/marketplace/updates/${updateId}/moderate`,
      request,
      { headers: this.getAuthHeaders() },
    );
  }

  // === Бизнес: удаление ===

  deleteBusiness(businessId: string): Observable<any> {
    return this.http.delete<any>(
      `${this.API_BASE_URL}/businesses/${businessId}`,
      { headers: this.getAuthHeaders() }
    );
  }

  // === Реферальная система ===

  recalculateMonth(request: RecalculateMonthRequest): Observable<RecalculationResponse> {
    return this.http.post<RecalculationResponse>(
      `${this.API_BASE_URL}/admin/referral/recalculate_month`,
      request,
      { headers: this.getAuthHeaders() }
    );
  }

  /** BE-723: POST /admin/referral/current_period/close — ручное закрытие текущего расчётного периода. */
  closeCurrentReferralPeriod(
    request: CloseCurrentReferralPeriodRequest
  ): Observable<ReferralPeriodClosureResponse> {
    return this.http.post<ReferralPeriodClosureResponse>(
      `${this.API_BASE_URL}/admin/referral/current_period/close`,
      request,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * GET /admin/referral/month_closure — расчётный период по ярлыку месяца: границы,
   * окно подтверждений и признак ручной границы (`reason` / `updated_by_user_id`).
   * Бэк период всегда материализует, поэтому `null` на деле не приходит.
   */
  getReferralMonthClosure(month: number, year: number): Observable<ReferralPeriodClosureResponse> {
    const params = new HttpParams().set('month', month).set('year', year);
    return this.http.get<ReferralPeriodClosureResponse>(
      `${this.API_BASE_URL}/admin/referral/month_closure`,
      { headers: this.getAuthHeaders(), params }
    );
  }

  /**
   * DELETE /admin/referral/month_closure — отмена ручной границы периода: граница
   * возвращается на 1-е число следующего месяца. Бэк разрешает это, только пока
   * граница не наступила и дефолтная граница не оказалась в прошлом; иначе 409
   * с текстом в `detail`.
   */
  deleteReferralMonthClosure(month: number, year: number): Observable<void> {
    const params = new HttpParams().set('month', month).set('year', year);
    return this.http.delete<void>(
      `${this.API_BASE_URL}/admin/referral/month_closure`,
      { headers: this.getAuthHeaders(), params }
    );
  }

  getRecalculations(limit = 50, offset = 0): Observable<RecalculationResponse[]> {
    const params = new HttpParams().set('limit', limit).set('offset', offset);
    return this.http.get<RecalculationResponse[]>(
      `${this.API_BASE_URL}/admin/referral/recalculations`,
      { headers: this.getAuthHeaders(), params }
    );
  }

  /**
   * BE-1150: порог игрового уровня друга для начисления универсального билета.
   * Бэк-часть — BE-1149; значение целое 1–10, дефолт 2.
   */
  getReferralTicketThreshold(): Observable<ReferralTicketThresholdResponse> {
    return this.http.get<ReferralTicketThresholdResponse>(
      `${this.API_BASE_URL}/admin/referral/ticket_reward_threshold`,
      { headers: this.getAuthHeaders() }
    );
  }

  updateReferralTicketThreshold(
    request: ReferralTicketThresholdUpdateRequest
  ): Observable<ReferralTicketThresholdResponse> {
    return this.http.put<ReferralTicketThresholdResponse>(
      `${this.API_BASE_URL}/admin/referral/ticket_reward_threshold`,
      request,
      { headers: this.getAuthHeaders() }
    );
  }

  getReserveFund(month?: number, year?: number): Observable<ReserveFundResponse> {
    let params = new HttpParams();
    if (month != null) params = params.set('month', month);
    if (year != null) params = params.set('year', year);
    return this.http.get<ReserveFundResponse>(
      `${this.API_BASE_URL}/admin/referral/reserve_fund`,
      { headers: this.getAuthHeaders(), params }
    );
  }

  /**
   * BE-761: GET /referral/calculator/rows — админ-калькулятор комиссий (эталонная xlsx-модель).
   * Только ADMIN/ROOT. date_from/date_to обязательны (UTC ISO datetime).
   */
  getReferralCalculatorRows(params: GetCalculatorRowsParams): Observable<CalculatorResponse> {
    let query = new HttpParams()
      .set('date_from', params.date_from)
      .set('date_to', params.date_to);
    if (params.business_id) query = query.set('business_id', params.business_id);
    if (params.limit != null) query = query.set('limit', String(params.limit));
    if (params.offset != null) query = query.set('offset', String(params.offset));
    return this.http.get<CalculatorResponse>(
      `${this.API_BASE_URL}/referral/calculator/rows`,
      { headers: this.getAuthHeaders(), params: query },
    );
  }

  /**
   * BE-1266: GET /admin/referral/agents/{agent_id} — агент по идентификатору.
   *
   * Была продуктовая `/referral/agents/{id}`, которая по одному маршруту принимала и uuid,
   * и реферальный код (разводила порядком роутов). Админский зеркальный набор разделил их
   * на два пути, поэтому по коду ищет отдельный `getReferralAgentByCode`.
   */
  getReferralAgent(agentId: string): Observable<ReferralAgentResponse> {
    const encodedId = encodeURIComponent(agentId.trim());
    return this.http.get<ReferralAgentResponse>(
      `${this.API_BASE_URL}/admin/referral/agents/${encodedId}`,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * BE-1266: GET /admin/referral/agents/by-code/{referral_code} — агент по реферальному коду.
   * Отдельный путь, а не перегрузка `getReferralAgent`: на админской стороне это разные ручки.
   */
  getReferralAgentByCode(referralCode: string): Observable<ReferralAgentResponse> {
    const encodedCode = encodeURIComponent(referralCode.trim());
    return this.http.get<ReferralAgentResponse>(
      `${this.API_BASE_URL}/admin/referral/agents/by-code/${encodedCode}`,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * BE-1266: GET /admin/referral/agents/structure — реферальная структура игрока-агента
   * (дерево «кого пригласил», вниз по уровням). player_id — чью структуру отдать,
   * level — ограничение глубины. Раньше ходили в продуктовую `/referral/agents/me/structure`.
   */
  getAgentStructure(playerId: string, level?: number): Observable<ReferralStructureResponse> {
    let params = new HttpParams().set('player_id', playerId);
    if (level != null) params = params.set('level', String(level));
    return this.http.get<ReferralStructureResponse>(
      `${this.API_BASE_URL}/admin/referral/agents/structure`,
      { headers: this.getAuthHeaders(), params },
    );
  }

  /**
   * BE-1266: GET /admin/referral/agents/referrals/search — поиск по команде агента (нижней
   * структуре) по ФИО или реферальному коду. player_id — чью структуру искать.
   */
  searchAgentReferrals(playerId: string, search: string, limit = 50, offset = 0): Observable<AgentReferralSearchResponse> {
    const params = new HttpParams()
      .set('player_id', playerId)
      .set('search', search)
      .set('limit', String(limit))
      .set('offset', String(offset));
    return this.http.get<AgentReferralSearchResponse>(
      `${this.API_BASE_URL}/admin/referral/agents/referrals/search`,
      { headers: this.getAuthHeaders(), params },
    );
  }

  /**
   * BE-1146/BE-1266: GET /admin/referral/agents/invited-businesses — бизнесы, приглашённые
   * агентом лично и через его команду; player_id — чьи считать.
   * Отдаёт ТОЛЬКО ACTIVE-бизнесы, приглашённые агентом; пагинации нет.
   */
  getAgentInvitedBusinesses(playerId: string): Observable<AgentInvitedBusinessesResponse> {
    const params = new HttpParams().set('player_id', playerId);
    return this.http.get<AgentInvitedBusinessesResponse>(
      `${this.API_BASE_URL}/admin/referral/agents/invited-businesses`,
      { headers: this.getAuthHeaders(), params },
    );
  }

  /** BE-451: POST /admin/referral/transfer */
  transferReferral(body: AdminReferralTransferRequest): Observable<AdminReferralTransferResponse> {
    return this.http.post<AdminReferralTransferResponse>(
      `${this.API_BASE_URL}/admin/referral/transfer`,
      body,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * BE-1379: POST /admin/referral/business-transfer — перенос бизнеса под другого агента.
   *
   * Нового пригласителя ручка принимает по `player_id` (не по `agent_id` и не по коду) —
   * как и перенос пользователя выше. Лишние поля запрещены схемой: `with_team` даст 422.
   *
   * ⚠️ Ошибки у ручки в OpenAPI не объявлены, но приходят: 400 «Бизнес уже закреплён за
   * этим агентом.», 403 не админ, 404 агента и 404 `business.not_found`. Причём агент
   * резолвится ПЕРВЫМ: при двух неверных идентификаторах вернётся ошибка агента.
   */
  transferBusinessReferrer(
    body: AdminBusinessReferrerTransferRequest,
  ): Observable<AdminBusinessReferrerTransferResponse> {
    return this.http.post<AdminBusinessReferrerTransferResponse>(
      `${this.API_BASE_URL}/admin/referral/business-transfer`,
      body,
      { headers: this.getAuthHeaders() },
    );
  }

  /** BE-452: GET /admin/players/stats */
  getAdminPlayersStats(): Observable<PlayerStatsResponse> {
    return this.http.get<PlayerStatsResponse>(
      `${this.API_BASE_URL}/admin/players/stats`,
      { headers: this.getAuthHeaders() },
    );
  }

  // === Эпик BE-1075: аналитика экрана «Статистика» (/admin/analytics/*, только Админ/root) ===

  /** Даты периода аналитики — московские `YYYY-MM-DD` без времени, как есть. */
  private analyticsPeriodParams(period: AnalyticsPeriod): HttpParams {
    return new HttpParams().set('date_from', period.date_from).set('date_to', period.date_to);
  }

  /** Расчётный месяц партнёрки — параметрами `month` / `year`, а не датами. */
  private analyticsMonthParams(month: AnalyticsSettlementMonth): HttpParams {
    return new HttpParams().set('month', month.month).set('year', month.year);
  }

  /** BE-1076: GET /admin/analytics/activity — DAU/WAU/MAU по дням и платформам. */
  getAnalyticsActivity(period: AnalyticsPeriod): Observable<AnalyticsActivityResponse> {
    return this.http.get<AnalyticsActivityResponse>(
      `${this.API_BASE_URL}/admin/analytics/activity`,
      { headers: this.getAuthHeaders(), params: this.analyticsPeriodParams(period) },
    );
  }

  /** BE-1076: GET /admin/analytics/retention — окно когорт уходит параметрами `cohort_from` / `cohort_to`. */
  getAnalyticsRetention(cohorts: AnalyticsPeriod): Observable<AnalyticsRetentionResponse> {
    const params = new HttpParams().set('cohort_from', cohorts.date_from).set('cohort_to', cohorts.date_to);
    return this.http.get<AnalyticsRetentionResponse>(
      `${this.API_BASE_URL}/admin/analytics/retention`,
      { headers: this.getAuthHeaders(), params },
    );
  }

  /** BE-1076: GET /admin/analytics/game — мини-игры, задания и начисления по дням. */
  getAnalyticsGame(period: AnalyticsPeriod): Observable<AnalyticsGameResponse> {
    return this.http.get<AnalyticsGameResponse>(
      `${this.API_BASE_URL}/admin/analytics/game`,
      { headers: this.getAuthHeaders(), params: this.analyticsPeriodParams(period) },
    );
  }

  /**
   * BE-1076: GET /admin/analytics/players — уровни, серии, онлайн и регистрации.
   * Заменяет устаревшую `getAdminPlayersStats` (окна по UTC); на бэке не кэшируется.
   */
  getAnalyticsPlayers(): Observable<AnalyticsPlayersResponse> {
    return this.http.get<AnalyticsPlayersResponse>(
      `${this.API_BASE_URL}/admin/analytics/players`,
      { headers: this.getAuthHeaders() },
    );
  }

  /** BE-1077: GET /admin/analytics/orders/summary — деньги, заказы и воронка промокодов. */
  getAnalyticsOrdersSummary(period: AnalyticsPeriod): Observable<AnalyticsOrdersSummaryResponse> {
    return this.http.get<AnalyticsOrdersSummaryResponse>(
      `${this.API_BASE_URL}/admin/analytics/orders/summary`,
      { headers: this.getAuthHeaders(), params: this.analyticsPeriodParams(period) },
    );
  }

  /** BE-1077: GET /admin/analytics/orders/top — топ бизнесов или промокодов по GMV; размер — по умолчанию бэка (20). */
  getAnalyticsOrdersTop(period: AnalyticsPeriod, by: AnalyticsOrdersTopDimension): Observable<AnalyticsOrdersTopResponse> {
    return this.http.get<AnalyticsOrdersTopResponse>(
      `${this.API_BASE_URL}/admin/analytics/orders/top`,
      { headers: this.getAuthHeaders(), params: this.analyticsPeriodParams(period).set('by', by) },
    );
  }

  /** BE-1077: GET /admin/analytics/orders/by-channel — заказы и GMV по каналам активации промокода. */
  getAnalyticsOrdersByChannel(period: AnalyticsPeriod): Observable<AnalyticsOrdersByChannelResponse> {
    return this.http.get<AnalyticsOrdersByChannelResponse>(
      `${this.API_BASE_URL}/admin/analytics/orders/by-channel`,
      { headers: this.getAuthHeaders(), params: this.analyticsPeriodParams(period) },
    );
  }

  /** BE-1078: GET /admin/analytics/cabinet/summary — бизнесы, магазины и акции по статусам; период — только для новых бизнесов. */
  getAnalyticsCabinetSummary(period: AnalyticsPeriod): Observable<AnalyticsCabinetSummaryResponse> {
    return this.http.get<AnalyticsCabinetSummaryResponse>(
      `${this.API_BASE_URL}/admin/analytics/cabinet/summary`,
      { headers: this.getAuthHeaders(), params: this.analyticsPeriodParams(period) },
    );
  }

  /** BE-1078: GET /admin/analytics/cabinet/invoices — счета по статусам и просрочка на сейчас, без периода. */
  getAnalyticsCabinetInvoices(): Observable<AnalyticsCabinetInvoicesResponse> {
    return this.http.get<AnalyticsCabinetInvoicesResponse>(
      `${this.API_BASE_URL}/admin/analytics/cabinet/invoices`,
      { headers: this.getAuthHeaders() },
    );
  }

  /** BE-1078: GET /admin/analytics/cabinet/moderation-funnel — шаги модерации акций и времена между ними. */
  getAnalyticsCabinetModerationFunnel(period: AnalyticsPeriod): Observable<AnalyticsModerationFunnelResponse> {
    return this.http.get<AnalyticsModerationFunnelResponse>(
      `${this.API_BASE_URL}/admin/analytics/cabinet/moderation-funnel`,
      { headers: this.getAuthHeaders(), params: this.analyticsPeriodParams(period) },
    );
  }

  /** BE-1107: GET /admin/analytics/lottery/summary — билеты, розыгрыши, фонд, участия и победы; тип режет все блоки. */
  getAnalyticsLotterySummary(query: AnalyticsLotteryQuery): Observable<AnalyticsLotterySummaryResponse> {
    const params = this.analyticsPeriodParams(query);
    return this.http.get<AnalyticsLotterySummaryResponse>(
      `${this.API_BASE_URL}/admin/analytics/lottery/summary`,
      { headers: this.getAuthHeaders(), params: query.raffle_type ? params.set('raffle_type', query.raffle_type) : params },
    );
  }

  /** BE-1108: GET /admin/analytics/overview — сводка «Обзора»: снимки и цифры за период в одном ответе. */
  getAnalyticsOverview(period: AnalyticsPeriod): Observable<AnalyticsOverviewResponse> {
    return this.http.get<AnalyticsOverviewResponse>(
      `${this.API_BASE_URL}/admin/analytics/overview`,
      { headers: this.getAuthHeaders(), params: this.analyticsPeriodParams(period) },
    );
  }

  /** BE-1079: GET /admin/analytics/referral/agents — агенты по статусам (сейчас) и рост структуры (за период). */
  getAnalyticsReferralAgents(period: AnalyticsPeriod): Observable<AnalyticsReferralAgentsResponse> {
    return this.http.get<AnalyticsReferralAgentsResponse>(
      `${this.API_BASE_URL}/admin/analytics/referral/agents`,
      { headers: this.getAuthHeaders(), params: this.analyticsPeriodParams(period) },
    );
  }

  /** BE-1079: GET /admin/analytics/referral/money — начисления, цикл оплаты и заявки на вывод за расчётный месяц. */
  getAnalyticsReferralMoney(month: AnalyticsSettlementMonth): Observable<AnalyticsReferralMoneyResponse> {
    return this.http.get<AnalyticsReferralMoneyResponse>(
      `${this.API_BASE_URL}/admin/analytics/referral/money`,
      { headers: this.getAuthHeaders(), params: this.analyticsMonthParams(month) },
    );
  }

  /** BE-1079: GET /admin/analytics/referral/top-agents — топ агентов за расчётный месяц, `limit` строк. */
  getAnalyticsReferralTopAgents(
    month: AnalyticsSettlementMonth,
    by: AnalyticsTopAgentsDimension,
    limit: number,
  ): Observable<AnalyticsTopAgentsResponse> {
    return this.http.get<AnalyticsTopAgentsResponse>(
      `${this.API_BASE_URL}/admin/analytics/referral/top-agents`,
      { headers: this.getAuthHeaders(), params: this.analyticsMonthParams(month).set('by', by).set('limit', limit) },
    );
  }

  /**
   * BE-764: GET /admin/players — пагинированный список игроков.
   * Серверный поиск/фильтр пока недоступны; params зарезервированы под
   * будущий `?search=` (см. GetAdminPlayersParams), фильтрация — на клиенте.
   */
  getAdminPlayers(params: GetAdminPlayersParams = {}): Observable<PaginatedPlayersResponse> {
    let httpParams = new HttpParams();
    if (params.offset != null) httpParams = httpParams.set('offset', String(params.offset));
    if (params.limit != null) httpParams = httpParams.set('limit', String(params.limit));
    return this.http.get<PaginatedPlayersResponse>(
      `${this.API_BASE_URL}/admin/players`,
      { headers: this.getAuthHeaders(), params: httpParams },
    );
  }

  // === BE-767: богатая карточка игрока (admin/users/player/:id) ===

  /** BE-767: GET /players/{player_id} — один игрок (PlayerView) для карточки. */
  getPlayerById(playerId: string): Observable<PlayerView> {
    return this.http.get<PlayerView>(
      `${this.API_BASE_URL}/players/${playerId}`,
      { headers: this.getAuthHeaders() },
    );
  }

  // === BE-769: действия над игроком ===

  /**
   * BE-769: заблокировать игрока — POST /admin/players/{player_id}/ban.
   * Тело не передаётся (204). Ошибки: 404 player.not_found, 409 player.already_banned.
   */
  banPlayer(playerId: string): Observable<void> {
    return this.http.post<void>(
      `${this.API_BASE_URL}/admin/players/${playerId}/ban`,
      {},
      { headers: this.getAuthHeaders() },
    );
  }

  /** BE-769: разблокировать игрока — POST /admin/players/{player_id}/unban. */
  unbanPlayer(playerId: string): Observable<void> {
    return this.http.post<void>(
      `${this.API_BASE_URL}/admin/players/${playerId}/unban`,
      {},
      { headers: this.getAuthHeaders() },
    );
  }

  /** BE-769: удалить игрока — DELETE /admin/players/{player_id}. */
  deletePlayer(playerId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.API_BASE_URL}/admin/players/${playerId}`,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * BE-769: пригласить бухгалтера — POST /admin/accountants/invite (201).
   * Тело `{ email }`. Ошибки: 403, 409 (email занят), 422.
   */
  inviteAccountant(data: InviteAccountantRequest): Observable<InviteManagerResponse> {
    return this.http.post<InviteManagerResponse>(
      `${this.API_BASE_URL}/admin/accountants/invite`,
      data,
      { headers: this.getAuthHeaders() },
    );
  }

  /** BE-767: GET /eco/{player_id} — балансы игрока (coins/tickets/offline_income). */
  getPlayerBalance(playerId: string): Observable<PlayerBalance> {
    return this.http.get<PlayerBalance>(
      `${this.API_BASE_URL}/eco/${playerId}`,
      { headers: this.getAuthHeaders() },
    );
  }

  /** BE-767: GET /eco/level/{player_id} — уровень и опыт игрока. */
  getPlayerLevel(playerId: string): Observable<PlayerLevel> {
    return this.http.get<PlayerLevel>(
      `${this.API_BASE_URL}/eco/level/${playerId}`,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * BE-767/BE-1266: GET /admin/referral/agents/{player_id}/profile — агент-профиль.
   * 404 → игрок не агент (блок «Партнёрка» не рендерится); 403 — отказ в доступе,
   * и путать его с «не агент» нельзя (см. `user-card.component.ts`).
   */
  getAgentProfile(playerId: string): Observable<AgentProfile> {
    return this.http.get<AgentProfile>(
      `${this.API_BASE_URL}/admin/referral/agents/${playerId}/profile`,
      { headers: this.getAuthHeaders() },
    );
  }

  /** BE-767: GET /orders — покупки игрока (пагинация по длине массива). */
  getPlayerOrders(params: GetPlayerOrdersParams): Observable<PlayerOrder[]> {
    let httpParams = new HttpParams().set('player_id', params.player_id);
    if (params.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params.date_to) httpParams = httpParams.set('date_to', params.date_to);
    if (params.limit != null) httpParams = httpParams.set('limit', String(params.limit));
    if (params.offset != null) httpParams = httpParams.set('offset', String(params.offset));
    return this.http.get<PlayerOrder[]>(
      `${this.API_BASE_URL}/orders`,
      { headers: this.getAuthHeaders(), params: httpParams },
    );
  }

  /** BE-767: GET /marketplace/{player_id}/purchases/promocodes — купленные промокоды. */
  getPlayerPromocodes(playerId: string, params: GetPlayerPromocodesParams = {}): Observable<PlayerPromocode[]> {
    let httpParams = new HttpParams();
    if (params.limit != null) httpParams = httpParams.set('limit', String(params.limit));
    if (params.offset != null) httpParams = httpParams.set('offset', String(params.offset));
    if (params.order_by) httpParams = httpParams.set('order_by', params.order_by);
    if (params.order_desc != null) httpParams = httpParams.set('order_desc', String(params.order_desc));
    return this.http.get<PlayerPromocode[]>(
      `${this.API_BASE_URL}/marketplace/${playerId}/purchases/promocodes`,
      { headers: this.getAuthHeaders(), params: httpParams },
    );
  }

  /** BE-767: GET /admin/wallet-history — история операций кошелька игрока. */
  getWalletHistory(params: GetWalletHistoryParams): Observable<WalletHistoryResponse> {
    let httpParams = new HttpParams();
    if (params.user_id) httpParams = httpParams.set('user_id', params.user_id);
    if (params.operation_type) httpParams = httpParams.set('operation_type', params.operation_type);
    if (params.target_type) httpParams = httpParams.set('target_type', params.target_type);
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.date_from) httpParams = httpParams.set('date_from', params.date_from);
    if (params.date_to) httpParams = httpParams.set('date_to', params.date_to);
    if (params.offset != null) httpParams = httpParams.set('offset', String(params.offset));
    if (params.limit != null) httpParams = httpParams.set('limit', String(params.limit));
    return this.http.get<WalletHistoryResponse>(
      `${this.API_BASE_URL}/admin/wallet-history`,
      { headers: this.getAuthHeaders(), params: httpParams },
    );
  }

  // === BE-474: raffle_builder (админ «Розыгрыши») ===
  private raffleBuildersAdminUrl(path = ''): string {
    return `${this.API_BASE_URL}/admin/raffle-builders${path}`;
  }

  /** GET одного розыгрыша: только здесь есть условия, победители и участие. */
  getKubishka(raffleId: string): Observable<KubishkaDetailResponse> {
    return this.http.get<KubishkaDetailResponse>(
      this.raffleBuildersAdminUrl(`/${raffleId}`),
      { headers: this.getAuthHeaders() },
    );
  }

  getKubishkas(status?: string, limit = 20, offset = 0): Observable<KubishkaResponse[]> {
    let params = new HttpParams().set('limit', limit).set('offset', offset);
    if (status) params = params.set('status', status);
    return this.http.get<KubishkaResponse[]>(
      this.raffleBuildersAdminUrl(),
      { headers: this.getAuthHeaders(), params },
    );
  }

  getKubishkaRaffleTypes(): Observable<KubishkaRaffleTypeOption[]> {
    return this.http.get<KubishkaRaffleTypeOption[]>(
      this.raffleBuildersAdminUrl('/raffle-types'),
      { headers: this.getAuthHeaders() },
    );
  }

  getKubishkaTicketTypes(): Observable<KubishkaTicketTypeOption[]> {
    return this.http.get<KubishkaTicketTypeOption[]>(
      this.raffleBuildersAdminUrl('/ticket-types'),
      { headers: this.getAuthHeaders() },
    );
  }

  getKubishkaDistributionModes(): Observable<KubishkaDistributionModeOption[]> {
    return this.http.get<KubishkaDistributionModeOption[]>(
      this.raffleBuildersAdminUrl('/distribution-modes'),
      { headers: this.getAuthHeaders() },
    );
  }

  /** Ответ — списочная схема: условий, победителей и участия в нём нет. */
  createKubishka(request: CreateKubishkaRequest): Observable<KubishkaResponse> {
    return this.http.post<KubishkaResponse>(
      this.raffleBuildersAdminUrl(),
      request,
      { headers: this.getAuthHeaders() },
    );
  }

  /** Ответ — списочная схема, как и у создания: за деталями нужен отдельный запрос. */
  updateKubishka(raffleId: string, request: UpdateKubishkaRequest): Observable<KubishkaResponse> {
    return this.http.put<KubishkaResponse>(
      this.raffleBuildersAdminUrl(`/${raffleId}`),
      request,
      { headers: this.getAuthHeaders() },
    );
  }

  deleteKubishka(raffleId: string): Observable<{}> {
    return this.http.delete<{}>(
      this.raffleBuildersAdminUrl(`/${raffleId}`),
      { headers: this.getAuthHeaders() },
    );
  }

  getKubishkaParticipants(raffleId: string, eligibleOnly = false, limit = 20, offset = 0): Observable<KubishkaParticipant[]> {
    let params = new HttpParams().set('limit', limit).set('offset', offset);
    if (eligibleOnly) params = params.set('eligible_only', true);
    return this.http.get<KubishkaParticipant[]>(
      this.raffleBuildersAdminUrl(`/${raffleId}/participants`),
      { headers: this.getAuthHeaders(), params },
    );
  }

  /**
   * BE-1256: `force` — query-параметр контракта, он пропускает проверки дат, включая
   * незакрытое окно добора. По умолчанию не отправляем вовсе: бэк сам решает по датам,
   * а лишний `force=false` в запросе выглядел бы как попытка обхода.
   */
  drawKubishka(raffleId: string, force = false): Observable<KubishkaDrawResponse> {
    let params = new HttpParams();
    if (force) params = params.set('force', true);
    return this.http.post<KubishkaDrawResponse>(
      this.raffleBuildersAdminUrl(`/${raffleId}/draw`),
      {},
      { headers: this.getAuthHeaders(), params },
    );
  }

  getKubishkaPrizeFundHistory(raffleId: string): Observable<PrizeFundContribution[]> {
    return this.http.get<PrizeFundContribution[]>(
      this.raffleBuildersAdminUrl(`/${raffleId}/prize-fund/history`),
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * BE-1207: публичные данные тиража. Единственный источник истории переносов —
   * в ответе детали розыгрыша её нет, а остальной протокол там уже есть.
   *
   * Путь без `/admin`: ручка публичная и работает без авторизации (проверено на dev).
   * Заголовки шлём всё равно — как и все прочие запросы сервиса; лишними они не бывают.
   */
  getRaffleFairness(raffleId: string): Observable<RaffleFairnessResponse> {
    return this.http.get<RaffleFairnessResponse>(
      `${this.API_BASE_URL}/raffle-builder/${raffleId}/fairness`,
      { headers: this.getAuthHeaders() },
    );
  }

  addKubishkaCondition(raffleId: string, request: CreateConditionRequest): Observable<KubishkaCondition> {
    return this.http.post<KubishkaCondition>(
      this.raffleBuildersAdminUrl(`/${raffleId}/conditions`),
      request,
      { headers: this.getAuthHeaders() },
    );
  }

  updateKubishkaCondition(raffleId: string, conditionId: string, request: UpdateConditionRequest): Observable<KubishkaCondition> {
    return this.http.put<KubishkaCondition>(
      this.raffleBuildersAdminUrl(`/${raffleId}/conditions/${conditionId}`),
      request,
      { headers: this.getAuthHeaders() },
    );
  }

  deleteKubishkaCondition(raffleId: string, conditionId: string): Observable<{}> {
    return this.http.delete<{}>(
      this.raffleBuildersAdminUrl(`/${raffleId}/conditions/${conditionId}`),
      { headers: this.getAuthHeaders() },
    );
  }

  // === BE-375: Скан промокода ===

  /**
   * POST /businesses/{business_id}/promocodes/scan
   * Шаг 1 (confirm_purchase=false) — расчёт скидки и комиссии.
   * Шаг 2 (confirm_purchase=true)  — подтверждение и создание заказа.
   */
  scanPromocode(businessId: string, payload: ScanPromocodeRequest): Observable<ScanPromocodeResponse> {
    return this.http.post<ScanPromocodeResponse>(
      `${this.API_BASE_URL}/businesses/${businessId}/promocodes/scan`,
      payload,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * BE-381: история заказов бизнеса по сканированиям промокодов.
   * GET /businesses/{business_id}/orders — сортировка фиксированно created_at DESC.
   */
  getBusinessOrders(
    businessId: string,
    params?: GetBusinessOrdersParams,
  ): Observable<BusinessOrdersListResponse> {
    let query = new HttpParams();
    if (params?.status) query = query.set('status', params.status);
    if (params?.date_from) query = query.set('date_from', params.date_from);
    if (params?.date_to) query = query.set('date_to', params.date_to);
    if (params?.promocode) query = query.set('promocode', params.promocode);
    if (params?.promotion_id) query = query.set('promotion_id', params.promotion_id);
    if (params?.limit !== undefined) query = query.set('limit', String(params.limit));
    if (params?.offset !== undefined) query = query.set('offset', String(params.offset));
    return this.http.get<BusinessOrdersListResponse>(
      `${this.API_BASE_URL}/businesses/${businessId}/orders`,
      { params: query, headers: this.getAuthHeaders() }
    );
  }

  /**
   * BE-772: бизнес-возврат по заказу.
   * POST /businesses/{business_id}/orders/{order_id}/refund — без тела.
   * observe:'response' — маршрутизация по HTTP-статусу (200/304 — успех), а не по телу ответа.
   * Тело может быть пустым (прокси), поэтому его игнорируем.
   */
  refundBusinessOrder(
    businessId: string,
    orderId: string,
  ): Observable<HttpResponse<unknown>> {
    return this.http.post<unknown>(
      `${this.API_BASE_URL}/businesses/${businessId}/orders/${orderId}/refund`,
      null,
      { headers: this.getAuthHeaders(), observe: 'response' }
    );
  }

  /**
   * BE-412/BE-720: сводка комиссии платформы (долг бизнеса).
   * GET /businesses/{business_id}/promocodes/summary — те же фильтры, что у /orders.
   * status=pending — к оплате (долг), completed — оплачено. Без дат — текущий месяц.
   */
  getPromocodesSummary(
    businessId: string,
    params?: GetPromocodeSummaryParams,
  ): Observable<PromocodeSummary> {
    let query = new HttpParams();
    if (params?.date_from) query = query.set('date_from', params.date_from);
    if (params?.date_to) query = query.set('date_to', params.date_to);
    if (params?.status) query = query.set('status', params.status);
    if (params?.promocode) query = query.set('promocode', params.promocode);
    if (params?.promotion_id) query = query.set('promotion_id', params.promotion_id);
    return this.http.get<PromocodeSummary>(
      `${this.API_BASE_URL}/businesses/${businessId}/promocodes/summary`,
      { params: query, headers: this.getAuthHeaders() }
    );
  }

  // === BE-370/BE-371: Manager tickets для бизнеса ===

  /** GET /businesses/{business_id}/manager-tickets — список обращений бизнеса. */
  getBusinessManagerTickets(
    businessId: string,
    params?: {
      status?: ManagerTicketStatus;
      limit?: number;
      offset?: number;
    },
  ): Observable<ManagerTicketsListResponse> {
    let query = new HttpParams();
    if (params?.status) query = query.set('status', params.status);
    if (params?.limit !== undefined) query = query.set('limit', String(params.limit));
    if (params?.offset !== undefined) query = query.set('offset', String(params.offset));
    return this.http.get<ManagerTicketsListResponse>(
      `${this.API_BASE_URL}/businesses/${businessId}/manager-tickets`,
      { params: query, headers: this.getAuthHeaders() }
    ).pipe(
      map((res) => ({
        items: (res?.items ?? []) as ManagerTicket[],
        total: res?.total ?? 0,
      }))
    );
  }

  /** POST /businesses/{business_id}/manager-tickets — создать обращение (GENERAL). */
  createBusinessGeneralTicket(
    businessId: string,
    payload: CreateGeneralTicketRequest,
  ): Observable<ManagerTicket> {
    return this.http.post<ManagerTicket>(
      `${this.API_BASE_URL}/businesses/${businessId}/manager-tickets`,
      payload,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * BE-682: POST /businesses/{business_id}/manager-tickets — создать обращение
   * с явным типом (например, kind=PROFILE_EDIT для запроса на редактирование профиля).
   */
  createBusinessManagerTicket(
    businessId: string,
    payload: CreateBusinessTicketRequest,
  ): Observable<ManagerTicket> {
    return this.http.post<ManagerTicket>(
      `${this.API_BASE_URL}/businesses/${businessId}/manager-tickets`,
      payload,
      { headers: this.getAuthHeaders() }
    );
  }

  // === BE-372: Manager tickets для админа/менеджера ===

  /** GET /admin/manager-tickets — список всех тикетов с фильтрами. */
  getAdminManagerTickets(params?: {
    status?: ManagerTicketStatus;
    kind?: ManagerTicketKind;
    assignee?: 'me' | 'unassigned' | 'any' | string;
    business_id?: string;
    limit?: number;
    offset?: number;
    order_by?: string;
    order_desc?: boolean;
  }): Observable<ManagerTicketsListResponse> {
    let query = new HttpParams();
    if (params?.status) query = query.set('status', params.status);
    if (params?.kind) query = query.set('kind', params.kind);
    if (params?.assignee) query = query.set('assignee', params.assignee);
    if (params?.business_id) query = query.set('business_id', params.business_id);
    if (params?.limit !== undefined) query = query.set('limit', String(params.limit));
    if (params?.offset !== undefined) query = query.set('offset', String(params.offset));
    if (params?.order_by) query = query.set('order_by', params.order_by);
    if (params?.order_desc !== undefined) query = query.set('order_desc', String(params.order_desc));
    return this.http.get<{ items?: unknown[]; total?: number }>(
      `${this.API_BASE_URL}/admin/manager-tickets`,
      { params: query, headers: this.getAuthHeaders() }
    ).pipe(
      map((res) => {
        const rawItems = res?.items ?? [];
        return {
          items: rawItems.map((item) => this.normalizeManagerTicket(item as Record<string, unknown>)),
          total: res?.total ?? rawItems.length,
        };
      })
    );
  }

  /** GET /admin/manager-tickets/{id} */
  getAdminManagerTicket(ticketId: string): Observable<ManagerTicket> {
    return this.http.get<Record<string, unknown>>(
      `${this.API_BASE_URL}/admin/manager-tickets/${ticketId}`,
      { headers: this.getAuthHeaders() },
    ).pipe(map((raw) => this.normalizeManagerTicket(raw)));
  }

  private normalizeManagerTicket(raw: Record<string, unknown>): ManagerTicket {
    const business = raw['business'] as Record<string, unknown> | undefined;
    const promotion = raw['related_promotion'] as Record<string, unknown> | undefined;
    const assigned = raw['assigned_manager'] as Record<string, unknown> | undefined;
    const payload = raw['payload'] as Record<string, unknown> | undefined;
    return {
      id: String(raw['id'] ?? ''),
      kind: raw['kind'] as ManagerTicket['kind'],
      status: raw['status'] as ManagerTicket['status'],
      business_id: (raw['business_id'] as string) ?? (business?.['id'] as string) ?? null,
      business_name:
        (raw['business_name'] as string)
        ?? (business?.['organization_name'] as string)
        ?? null,
      user_suggestion:
        (raw['user_suggestion'] as string) ?? (payload?.['user_suggestion'] as string) ?? null,
      business_comment: (raw['business_comment'] as string) ?? null,
      manager_comment: (raw['manager_comment'] as string) ?? null,
      assignee_id: (raw['assignee_id'] as string) ?? (assigned?.['id'] as string) ?? null,
      assignee_name:
        (raw['assignee_name'] as string)
        ?? (assigned?.['email'] as string)
        ?? null,
      promotion_id: (raw['promotion_id'] as string) ?? (promotion?.['id'] as string) ?? null,
      related_promotion_title: (promotion?.['title'] as string) ?? null,
      parent_category_id: (payload?.['parent_category_id'] as string) ?? null,
      payload: payload ?? null,
      created_at: String(raw['created_at'] ?? ''),
      updated_at: (raw['updated_at'] as string) ?? (raw['resolved_at'] as string) ?? null,
    };
  }

  /** POST /admin/categories */
  createAdminCategory(payload: AdminCreateCategoryRequest): Observable<MarketplaceCategory> {
    return this.http.post<MarketplaceCategory>(
      `${this.API_BASE_URL}/admin/categories`,
      payload,
      { headers: this.getAuthHeaders() },
    );
  }

  /** PUT /admin/categories/{id} */
  updateAdminCategory(categoryId: string, payload: AdminUpdateCategoryRequest): Observable<MarketplaceCategory> {
    return this.http.put<MarketplaceCategory>(
      `${this.API_BASE_URL}/admin/categories/${categoryId}`,
      payload,
      { headers: this.getAuthHeaders() },
    );
  }

  /** DELETE /admin/categories/{id} */
  deleteAdminCategory(categoryId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.API_BASE_URL}/admin/categories/${categoryId}`,
      { headers: this.getAuthHeaders() },
    );
  }

  /** POST /admin/manager-tickets/{id}/resolve — закрыть с решением. */
  resolveManagerTicket(ticketId: string, payload: ResolveTicketRequest = {}): Observable<ManagerTicket> {
    return this.http.post<ManagerTicket>(
      `${this.API_BASE_URL}/admin/manager-tickets/${ticketId}/resolve`,
      payload,
      { headers: this.getAuthHeaders() }
    );
  }

  /** POST /admin/manager-tickets/{id}/dismiss — отклонить (manager_comment обязателен). */
  dismissManagerTicket(ticketId: string, payload: DismissTicketRequest): Observable<ManagerTicket> {
    return this.http.post<ManagerTicket>(
      `${this.API_BASE_URL}/admin/manager-tickets/${ticketId}/dismiss`,
      payload,
      { headers: this.getAuthHeaders() }
    );
  }

  /** POST /admin/manager-tickets/{id}/assign — назначить менеджера или снять (assignee_id=null). */
  assignManagerTicket(ticketId: string, payload: AssignTicketRequest): Observable<ManagerTicket> {
    return this.http.post<ManagerTicket>(
      `${this.API_BASE_URL}/admin/manager-tickets/${ticketId}/assign`,
      payload,
      { headers: this.getAuthHeaders() }
    );
  }

  // === BE-427: Бухгалтерия — счета ===

  getAccountingInvoices(params?: AccountingInvoicesQuery): Observable<InvoiceListResponse> {
    let query = new HttpParams();
    if (params?.status) query = query.set('status', params.status);
    if (params?.business_id) query = query.set('business_id', params.business_id);
    if (params?.date_from) query = query.set('date_from', params.date_from);
    if (params?.date_to) query = query.set('date_to', params.date_to);
    if (params?.is_overdue !== undefined) query = query.set('is_overdue', String(params.is_overdue));
    if (params?.counterparty_type) query = query.set('counterparty_type', params.counterparty_type);
    if (params?.sort_by) query = query.set('sort_by', params.sort_by);
    if (params?.sort_order) query = query.set('sort_order', params.sort_order);
    if (params?.limit !== undefined) query = query.set('limit', String(params.limit));
    if (params?.offset !== undefined) query = query.set('offset', String(params.offset));
    return this.http.get<InvoiceListResponse>(
      `${this.API_BASE_URL}/accounting/invoices`,
      { params: query, headers: this.getAuthHeaders() }
    );
  }

  getAccountingInvoice(invoiceId: string): Observable<InvoiceDetail> {
    return this.http.get<InvoiceDetail>(
      `${this.API_BASE_URL}/accounting/invoices/${invoiceId}`,
      { headers: this.getAuthHeaders() }
    );
  }

  confirmAccountingInvoice(invoiceId: string, payload: ConfirmInvoiceRequest): Observable<InvoiceDetail> {
    return this.http.post<InvoiceDetail>(
      `${this.API_BASE_URL}/accounting/invoices/${invoiceId}/confirm`,
      payload,
      { headers: this.getAuthHeaders() }
    );
  }

  rescheduleAccountingInvoice(invoiceId: string, payload: RescheduleInvoiceRequest = {}): Observable<InvoiceDetail> {
    return this.http.post<InvoiceDetail>(
      `${this.API_BASE_URL}/accounting/invoices/${invoiceId}/reschedule`,
      payload,
      { headers: this.getAuthHeaders() }
    );
  }

  /** BE-607: ручная смена статуса счёта (только ADMIN; не-ADMIN получает 403). */
  setAccountingInvoiceStatus(invoiceId: string, status: InvoiceStatus): Observable<InvoiceDetail> {
    return this.http.post<InvoiceDetail>(
      `${this.API_BASE_URL}/accounting/invoices/${invoiceId}/status`,
      { status },
      { headers: this.getAuthHeaders() }
    );
  }

  /** BE-429 / BE-856: запрос счёта бизнесом — обязателен `shop_id` (1 счёт = 1 магазин). */
  requestBusinessAccountingInvoice(businessId: string, payload: RequestAccountingInvoicePayload): Observable<InvoiceDetail> {
    return this.http.post<InvoiceDetail>(
      `${this.API_BASE_URL}/businesses/${businessId}/accounting-invoices/request`,
      payload,
      { headers: this.getAuthHeaders() }
    );
  }

  /** BE-734: список счетов бизнеса (история) — GET /businesses/{id}/invoices. */
  getBusinessInvoices(businessId: string, params?: BusinessInvoicesQuery): Observable<InvoiceListResponse> {
    let query = new HttpParams();
    if (params?.status) query = query.set('status', params.status);
    if (params?.shop_id) query = query.set('shop_id', params.shop_id);
    if (params?.date_from) query = query.set('date_from', params.date_from);
    if (params?.date_to) query = query.set('date_to', params.date_to);
    if (params?.is_overdue !== undefined) query = query.set('is_overdue', String(params.is_overdue));
    if (params?.sort_by) query = query.set('sort_by', params.sort_by);
    if (params?.sort_order) query = query.set('sort_order', params.sort_order);
    if (params?.limit !== undefined) query = query.set('limit', String(params.limit));
    if (params?.offset !== undefined) query = query.set('offset', String(params.offset));
    return this.http.get<InvoiceListResponse>(
      `${this.API_BASE_URL}/businesses/${businessId}/invoices`,
      { params: query, headers: this.getAuthHeaders() }
    );
  }

  /** BE-734: деталь счёта бизнеса (с заказами) — GET /businesses/{id}/invoices/{invoice_id}. */
  getBusinessInvoiceDetail(businessId: string, invoiceId: string): Observable<InvoiceDetail> {
    return this.http.get<InvoiceDetail>(
      `${this.API_BASE_URL}/businesses/${businessId}/invoices/${invoiceId}`,
      { headers: this.getAuthHeaders() }
    );
  }

  /** BE-734: агрегат счетов для кнопки «К оплате» — GET /businesses/{id}/invoices/summary. */
  getBusinessAccountingSummary(businessId: string): Observable<BusinessAccountingSummaryResponse> {
    return this.http.get<BusinessAccountingSummaryResponse>(
      `${this.API_BASE_URL}/businesses/${businessId}/invoices/summary`,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * BE-337: список категорий маркетплейса (справочник для выбора категории акции).
   * GET /marketplace/categories?level=1
   */
  getMarketplaceCategories(level: 1 | 2 = 1, parentId?: string): Observable<MarketplaceCategory[]> {
    let params = new HttpParams().set('level', String(level));
    if (parentId) params = params.set('parent_id', parentId);
    return this.http.get<{ categories: MarketplaceCategory[] }>(
      `${this.API_BASE_URL}/marketplace/categories`,
      { params, headers: this.getAuthHeaders() }
    ).pipe(map((r) => r?.categories || []));
  }

  /**
   * Закрыть игровой сезон — POST /admin/season/close, роль Админ.
   *
   * Обменивает монеты игроков на билеты по курсу и обнуляет прибыль в час.
   * ☠️ Операция НЕОБРАТИМА: отката нет, повторно закрыть тот же `label` бэк
   * не даст (409). Поэтому `dry_run` в теле обязателен и осознан — умолчания
   * здесь нет намеренно, чтобы боевой прогон нельзя было запустить опиской.
   *
   * Таймаут и оверлей — как у рассылки (`notificationDispatchContext`), и по той
   * же причине: закрытие идёт по всем игрокам, дефолтные 30 с его оборвут, а
   * полноэкранное «нет связи» на обрыве подтолкнуло бы запустить закрытие
   * второй раз. Обрыв на клиенте операцию на сервере не отменяет.
   */
  closeSeason(body: SeasonCloseRequest): Observable<SeasonCloseResponse> {
    return this.http.post<SeasonCloseResponse>(
      `${this.API_BASE_URL}/admin/season/close`,
      body,
      {
        headers: this.getAuthHeaders(),
        context: notificationDispatchContext(SEASON_CLOSE_TIMEOUT_MS),
      },
    );
  }

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  /** Multipart: только Bearer — boundary выставит браузер. */
  private getMultipartAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
  }
}
